import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  Plus, 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Edit,
  Clock,
  User,
  Users,
  Plane,
  AlertCircle,
  MoreVertical,
  X,
  TrendingUp,
  Copy,
  RotateCw,
  CheckCircle,
  Shield,
  Home,
  Building,
  ArrowLeftRight,
  Info,
  Send,
  RefreshCw,
  Lock,
  Unlock,
  Download,
  CalendarDays,
  AlertTriangle as WarningIcon
} from 'lucide-react';
import RotationManagement from './RotationManagement';
import MonthlyCalendar from './MonthlyCalendar';
import ScheduleConflictModal from './ScheduleConflictModal';
import { WFHLaterDatePicker } from './WFHLaterDatePicker';
import { PartialPTOFields } from './PartialPTOFields';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { formatDateSafe, formatDateWithWeekday, parseLocalDate, toLocalYMD } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WFH_QUICK_LIST_DAYS = 14;

const ScheduleManager = () => {
  const { user } = useAuth();
  
  // Restore persisted state from sessionStorage
  const [currentDate, setCurrentDate] = useState(() => {
    const saved = sessionStorage.getItem('schedule_currentDate');
    if (saved) {
      const d = new Date(saved);
      return isNaN(d.getTime()) ? new Date() : d;
    }
    return new Date();
  });
  const [schedules, setSchedules] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]); // Cache for pre-loaded schedules
  const [holidays, setHolidays] = useState([]); // Company holidays for visual indicators
  const [loadedDateRange, setLoadedDateRange] = useState({ start: null, end: null });
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false); // Form submit in progress (separate from grid loading)
  const [activities, setActivities] = useState([]); // User activities
  const [activityTypes, setActivityTypes] = useState([]); // Available activity types
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    user_id: '',
    user_ids: [],
    date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '10:00',
    all_day: false,
    activity_type: '',
    title: '',
    description: '',
    location: '',
    client_name: ''
  });
  const [editingActivity, setEditingActivity] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]); // Dynamic roles from backend
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Check if current user has management privileges
  const isManagerOrAdmin = user && (user.role === 'manager' || user.role === 'admin' || user.role === 'team_lead');
  const [formData, setFormData] = useState({
    user_id: '',
    shift_template_id: '',
    date: '',
    start_time: '',
    end_time: '',
    shift_type: 'regular',
    role: 'tier1',
    work_location: 'office',
    notes: ''
  });
  const [showQuickSchedule, setShowQuickSchedule] = useState(false);
  const [quickScheduleData, setQuickScheduleData] = useState({
    user_id: '',
    shift_template_id: '',
    days: [], // Array of selected days
    notes: ''
  });
  const [showPTOModal, setShowPTOModal] = useState(false);
  const [ptoSubmitting, setPtoSubmitting] = useState(false);
  const [ptoRequests, setPtoRequests] = useState([]);
  const [ptoForm, setPtoForm] = useState({
    start_date: '',
    end_date: '',
    pto_type: 'pto',
    reason: '',
    is_partial_day: false,
    start_time: '09:00',
    end_time: '17:00',
    worked_start_time: '',
    worked_end_time: '',
    exclude_weekends: true
  });
  const [showWFHModal, setShowWFHModal] = useState(false);
  const [wfhForm, setWfhForm] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    hours: 8,  // Default to full day (8 hours)
    wfh_type: '',  // No default - user must select
    wfh_period: 'morning'  // For half-day: which part is remote
  });
  const [selectedWFHShifts, setSelectedWFHShifts] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [wfhUserId, setWfhUserId] = useState(null);
  const [flexBankInfo, setFlexBankInfo] = useState(null);
  const [flexBankSettings, setFlexBankSettings] = useState({ allowed_hours: [4, 8] });
  const [isHistoricalWFH, setIsHistoricalWFH] = useState(false);
  const [wfhTypes, setWfhTypes] = useState([
    { value: 'flex_bank', display_name: 'Flex Bank', description: 'Use your Flex Bank days', requires_balance: true, manager_only: false },
    { value: 'on_call', display_name: 'On-Call WFH', description: 'Work from home while on-call', requires_balance: false, manager_only: false },
    { value: 'business_directed', display_name: 'Business Directed', description: 'Company-directed remote work', requires_balance: false, manager_only: true }
  ]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const isPtoBlockOpen = ['pto', 'pending_pto'].includes(editingSchedule?.shift_type);
  const isPendingPtoOpen = editingSchedule?.shift_type === 'pending_pto';
  const [editingPtoRequest, setEditingPtoRequest] = useState(null);
  const [ptoDetailEdit, setPtoDetailEdit] = useState(null); // { pto_type, reason } while editing
  const [ptoDetailSaving, setPtoDetailSaving] = useState(false);
  const [ptoRemoveReason, setPtoRemoveReason] = useState('');
  
  // Manager delete functionality state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [showClearScheduleModal, setShowClearScheduleModal] = useState(false);
  const [employeeToClear, setEmployeeToClear] = useState(null);
  const [clearScheduleRange, setClearScheduleRange] = useState('week'); // 'week', 'all', 'custom'
  const [clearScheduleShiftType, setClearScheduleShiftType] = useState('all'); // 'all', 'regular', 'on_call'
  const [clearScheduleDateRange, setClearScheduleDateRange] = useState({ start: '', end: '' });
  
  // Smart cache for recently viewed weeks (improves navigation performance)
  const [scheduleCache, setScheduleCache] = useState({});
  const cacheRef = useRef({});
  const scheduleGridRef = useRef(null);
  
  // Multiple day selection state for shift modal
  const [enableMultipleDays, setEnableMultipleDays] = useState(false);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]); // [0-6] Sunday = 0
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const [scheduleWeeksCount, setScheduleWeeksCount] = useState(1);
  const [scheduleDurationMode, setScheduleDurationMode] = useState('weeks'); // 'weeks' or 'endDate'
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  
  // Manager PTO assignment state
  const [selectedEmployeeForPTO, setSelectedEmployeeForPTO] = useState(null);
  // Manager WFH assignment state
  const [selectedEmployeeForWFH, setSelectedEmployeeForWFH] = useState(null);
  const [ptoTypes, setPtoTypes] = useState([]); // Dynamic PTO types from backend
  
  // Shift swap request from schedule grid
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [scheduleToSwap, setScheduleToSwap] = useState(null);
  const [swapReason, setSwapReason] = useState('');
  const [swapRequiresApproval, setSwapRequiresApproval] = useState(true);
  
  // Role filter state
  const [roleFilter, setRoleFilter] = useState(() => sessionStorage.getItem('schedule_roleFilter') || 'all');
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Conflict detection state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [detectedConflicts, setDetectedConflicts] = useState(null);
  const [pendingScheduleData, setPendingScheduleData] = useState(null); // Store data while waiting for conflict resolution
  const [conflictResolutionMode, setConflictResolutionMode] = useState(null); // 'single' or 'multiple'
  
  // Daily Schedule View state
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('schedule_viewMode') || 'weekly');
  const [shiftTypeFilter, setShiftTypeFilter] = useState(() => sessionStorage.getItem('schedule_shiftTypeFilter') || 'all');
  const [dailyViewFilters, setDailyViewFilters] = useState({
    roleFilter: 'all',
    teamFilter: 'all'
  });
  
  // Copy-week functionality state
  const [showCopyWeekModal, setShowCopyWeekModal] = useState(false);
  const [copyWeekData, setCopyWeekData] = useState({
    sourceWeek: '',
    targetWeek: '',
    overwriteExisting: false,
    copyMode: 'entire', // 'entire', 'employees', 'days'
    selectedEmployees: [],
    selectedDays: []
  });
  const [showRotationsModal, setShowRotationsModal] = useState(false);
  // Copy-week overlap resolution state
  const [copyOverlap, setCopyOverlap] = useState({ open: false, plan: [], conflicts: [], headers: null, modeText: '', expanded: false });
  
  // Schedule lock state
  const [weekLocked, setWeekLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  
  // Max hours state
  const [hoursData, setHoursData] = useState(null);
  const [scheduleRules, setScheduleRules] = useState(null);
  const [lunchPolicy, setLunchPolicy] = useState(null);
  const [partialPtoValid, setPartialPtoValid] = useState(false);
  
  // Drag-and-drop functionality state
  const [draggedShift, setDraggedShift] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // When a shift is dropped onto a cell that already has a shift, pause and ask
  // the manager how to resolve it (swap / replace / keep both).
  const [dropDecision, setDropDecision] = useState(null);
  // Daily Timeline anchor: null = live "today" (rolling 12h back / 24h ahead);
  // a Date = view that specific day (36h window: day 00:00 → next day 12:00).
  const [timelineAnchor, setTimelineAnchor] = useState(null);

  // Persist key state to sessionStorage so browser refresh doesn't lose position
  useEffect(() => {
    sessionStorage.setItem('schedule_currentDate', currentDate.toISOString());
  }, [currentDate]);
  
  useEffect(() => {
    sessionStorage.setItem('schedule_roleFilter', roleFilter);
  }, [roleFilter]);
  
  useEffect(() => {
    sessionStorage.setItem('schedule_viewMode', viewMode);
  }, [viewMode]);
  
  useEffect(() => {
    sessionStorage.setItem('schedule_shiftTypeFilter', shiftTypeFilter);
  }, [shiftTypeFilter]);


  // Check for editActivity query parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activityId = params.get('editActivity');
    
    if (activityId && activities.length > 0) {
      const activityToEdit = activities.find(a => a.id === activityId);
      if (activityToEdit) {
        // Populate the activity form with existing data
        setActivityForm({
          user_id: activityToEdit.user_id,
          user_ids: [], // Can't edit bulk activities from dashboard
          date: activityToEdit.date,
          start_time: activityToEdit.start_time,
          end_time: activityToEdit.end_time,
          activity_type: activityToEdit.activity_type,
          title: activityToEdit.title,
          description: activityToEdit.description || '',
          location: activityToEdit.location || '',
          client_name: activityToEdit.client_name || ''
        });
        setEditingActivity(activityToEdit);
        setShowActivityModal(true);
        
        // Clean up URL
        window.history.replaceState({}, '', '/schedule');
      }
    }
  }, [activities]);

  useEffect(() => {
    // Fetch employees and templates once on mount
    fetchEmployeesAndTemplates();
    // Fetch lunch policy for hours calculations
    fetchLunchPolicy();
    // Force refresh on mount to ensure fresh data (cache may be stale from other pages)
    fetchSchedulesForCurrentWeek(true).then(() => {
      // Restore scroll position after data loads and renders
      const savedScrollY = sessionStorage.getItem('schedule_scrollY');
      if (savedScrollY) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, parseInt(savedScrollY, 10));
          });
        });
      }
    });
  }, []);
  
  // Save scroll position periodically so it persists through browser refresh
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem('schedule_scrollY', String(window.pageYOffset || document.documentElement.scrollTop));
      }, 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Fetch holidays for visual indicators
  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get the start and end of the current week to determine which years to fetch
      const weekStartStr = getStartOfWeek(currentDate);
      const [year, month, day] = weekStartStr.split('-').map(Number);
      const weekStart = new Date(year, month - 1, day);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startYear = weekStart.getFullYear();
      const endYear = weekEnd.getFullYear();
      
      // If the week spans two years, fetch holidays for both years
      let allHolidays = [];
      if (startYear === endYear) {
        const response = await axios.get(`${API}/company-holidays?year=${startYear}`, { headers });
        allHolidays = response.data || [];
      } else {
        // Week spans two years (e.g., Dec 30 - Jan 5)
        const response1 = await axios.get(`${API}/company-holidays?year=${startYear}`, { headers });
        const response2 = await axios.get(`${API}/company-holidays?year=${endYear}`, { headers });
        allHolidays = [...(response1.data || []), ...(response2.data || [])];
      }
      
      setHolidays(allHolidays);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/activity-types`, { headers });
      setActivityTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch activity types:', error);
    }
  };

  const fetchActivities = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/activities?start_date=${startDate}&end_date=${endDate}`, 
        { headers }
      );
      setActivities(response.data || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  useEffect(() => {
    // When date changes, fetch schedules for the new week (preserve scroll position)
    // Save scroll before fetch, restore after — so navigating weeks keeps you at the same employee row
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const gridScroll = scheduleGridRef.current ? {
      top: scheduleGridRef.current.scrollTop,
      left: scheduleGridRef.current.scrollLeft
    } : null;
    
    fetchSchedulesForCurrentWeek().then(() => {
      // Restore scroll after new week loads (skip for daily view which has its own scroll)
      if (viewMode !== 'daily') {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
          if (gridScroll && scheduleGridRef.current) {
            scheduleGridRef.current.scrollTop = gridScroll.top;
          }
        });
      }
    });
    fetchHolidays();
    
    // Fetch activities for the current week
    const weekStartStr = getStartOfWeek(currentDate);
    const [year, month, day] = weekStartStr.split('-').map(Number);
    const weekStart = new Date(year, month - 1, day);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startStr = weekStart.toISOString().split('T')[0];
    const endStr = weekEnd.toISOString().split('T')[0];
    fetchActivities(startStr, endStr);
  }, [currentDate]);
  
  // Fetch activity types only once on mount
  useEffect(() => {
    fetchActivityTypes();
  }, []);

  // Load the exact data the Daily Timeline window needs whenever the daily view
  // is shown or the selected day changes. Decoupled from the weekly currentDate
  // and uses a narrow date range so nothing gets truncated by the backend cap.
  useEffect(() => {
    if (viewMode === 'daily') {
      fetchDailyViewSchedules();
    }
  }, [viewMode, timelineAnchor]);

  // Auto-scroll to current hour (past 12 hours of timeline to the left)
  useEffect(() => {
    if (viewMode === 'daily' && !loading && allSchedules.length > 0) {
      // Use multiple approaches to ensure scroll works
      const scrollToCurrentHour = () => {
        const container = document.getElementById('daily-timeline-container');
        if (container) {
          // Timeline starts 12 hours ago, scroll forward to show current hour first
          // Find the table to get accurate column widths
          const table = container.querySelector('table');
          if (table) {
            const firstHourCell = table.querySelector('th:nth-child(2)'); // First hour column after employee column
            if (firstHourCell) {
              const columnWidth = firstHourCell.offsetWidth;
              const scrollPosition = 11 * columnWidth; // 11 columns forward so current hour is fully visible
              container.scrollLeft = scrollPosition;
              console.log(`✅ Daily view: scrolled ${scrollPosition}px (${columnWidth}px per hour x 11 hours)`);
              
              // Verify it stuck
              setTimeout(() => {
                if (Math.abs(container.scrollLeft - scrollPosition) > 10) {
                  console.warn(`⚠️ Scroll didn't stick: wanted ${scrollPosition}px, got ${container.scrollLeft}px`);
                  container.scrollLeft = scrollPosition; // Try again
                }
              }, 100);
            }
          }
        }
      };
      
      // Try multiple times with different delays
      setTimeout(scrollToCurrentHour, 200);
      setTimeout(scrollToCurrentHour, 500);
      setTimeout(scrollToCurrentHour, 1000);
    }
  }, [viewMode, loading, allSchedules.length]);

  // Simplified: Fetch schedules for currently viewed week with smart caching
  const fetchSchedulesForCurrentWeek = async (forceRefresh = false, preserveScroll = false) => {
    // Save scroll position of window and any scroll containers
    let savedScrollY = 0;
    let savedGridScroll = null;
    
    if (preserveScroll) {
      savedScrollY = window.pageYOffset || document.documentElement.scrollTop;
      if (scheduleGridRef.current) {
        savedGridScroll = {
          top: scheduleGridRef.current.scrollTop,
          left: scheduleGridRef.current.scrollLeft
        };
      }
    }
    
    try {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = getEndOfWeek(currentDate);
      const cacheKey = `${startOfWeek}_${endOfWeek}`;
      
      // Check cache first (unless force refresh)
      if (!forceRefresh && cacheRef.current[cacheKey]) {
        console.log(`⚡ Using cached schedules for week: ${startOfWeek} to ${endOfWeek}`);
        setSchedules(cacheRef.current[cacheKey]);
        setLoading(false);
        
        // Restore scroll position (but not for daily view - it has its own scroll logic)
        if (preserveScroll && viewMode !== 'daily') {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY);
            if (savedGridScroll && scheduleGridRef.current) {
              scheduleGridRef.current.scrollTop = savedGridScroll.top;
              scheduleGridRef.current.scrollLeft = savedGridScroll.left;
            }
          });
        }
        return;
      }
      
      // Only show loading spinner for initial loads, not for preserveScroll refreshes
      // (showing spinner destroys scroll position by replacing DOM content)
      if (!preserveScroll) {
        setLoading(true);
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        window.location.href = '/';
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log(`📅 Fetching schedules for week: ${startOfWeek} to ${endOfWeek}`);
      
      const schedulesRes = await axios.get(
        `${API}/schedules?date_from=${startOfWeek}&date_to=${endOfWeek}`, 
        { headers }
      );
      
      const rawSchedules = schedulesRes.data || [];
      console.log(`📊 Loaded ${rawSchedules.length} schedules from API`);
      
      // Remove duplicates based on unique schedule ID
      const uniqueSchedules = [];
      const seenIds = new Set();
      
      rawSchedules.forEach(schedule => {
        if (!seenIds.has(schedule.id)) {
          seenIds.add(schedule.id);
          uniqueSchedules.push(schedule);
        } else {
          console.warn(`⚠️ Duplicate schedule ID found: ${schedule.id}`);
        }
      });
      
      console.log(`✅ After deduplication: ${uniqueSchedules.length} unique schedules`);
      
      // Update cache (keep last 10 weeks in memory)
      const newCache = { ...cacheRef.current };
      const cacheKeys = Object.keys(newCache);
      if (cacheKeys.length >= 10) {
        // Remove oldest entry
        delete newCache[cacheKeys[0]];
      }
      newCache[cacheKey] = uniqueSchedules;
      cacheRef.current = newCache;
      
      setSchedules(uniqueSchedules);
      setAllSchedules(uniqueSchedules); // CRITICAL: Also update allSchedules for daily view
      
      // Fetch lock status and hours data for current week
      try {
        const weekStart = getStartOfWeek(currentDate);
        const [lockRes, hoursRes] = await Promise.all([
          axios.get(`${API}/schedule-locks/${weekStart}`, { headers }),
          axios.get(`${API}/schedule-hours-check?week_start=${weekStart}`, { headers })
        ]);
        setWeekLocked(lockRes.data?.locked || false);
        setLockInfo(lockRes.data?.lock || null);
        setHoursData(hoursRes.data || null);
      } catch (e) { /* non-critical */ }
      
      // Restore scroll position after React updates DOM (but not for daily view - it has its own scroll logic)
      if (preserveScroll && viewMode !== 'daily') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY);
            if (savedGridScroll && scheduleGridRef.current) {
              scheduleGridRef.current.scrollTop = savedGridScroll.top;
              scheduleGridRef.current.scrollLeft = savedGridScroll.left;
            }
          });
        });
      }
      
      // Prefetch adjacent weeks for faster navigation
      prefetchAdjacentWeeks(startOfWeek, endOfWeek, headers);
      
    } catch (error) {
      console.error('Failed to load schedules:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error('Session expired');
        window.location.href = '/';
      } else {
        toast.error('Failed to load schedules');
      }
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Prefetch adjacent weeks in background for faster navigation
  const prefetchAdjacentWeeks = async (currentStart, currentEnd, headers) => {
    try {
      // Calculate previous and next week dates
      const currentStartDate = new Date(currentStart);
      const currentEndDate = new Date(currentEnd);
      
      // Previous week
      const prevStart = new Date(currentStartDate);
      prevStart.setDate(prevStart.getDate() - 7);
      const prevEnd = new Date(currentEndDate);
      prevEnd.setDate(prevEnd.getDate() - 7);
      const prevCacheKey = `${prevStart.toISOString().split('T')[0]}_${prevEnd.toISOString().split('T')[0]}`;
      
      // Next week
      const nextStart = new Date(currentStartDate);
      nextStart.setDate(nextStart.getDate() + 7);
      const nextEnd = new Date(currentEndDate);
      nextEnd.setDate(nextEnd.getDate() + 7);
      const nextCacheKey = `${nextStart.toISOString().split('T')[0]}_${nextEnd.toISOString().split('T')[0]}`;
      
      // Only fetch if not already cached
      const fetchPromises = [];
      
      if (!cacheRef.current[prevCacheKey]) {
        fetchPromises.push(
          axios.get(`${API}/schedules?date_from=${prevStart.toISOString().split('T')[0]}&date_to=${prevEnd.toISOString().split('T')[0]}`, { headers })
            .then(res => {
              cacheRef.current[prevCacheKey] = res.data || [];
              console.log(`🔮 Prefetched previous week: ${prevCacheKey}`);
            })
            .catch(() => {}) // Silently fail for prefetch
        );
      }
      
      if (!cacheRef.current[nextCacheKey]) {
        fetchPromises.push(
          axios.get(`${API}/schedules?date_from=${nextStart.toISOString().split('T')[0]}&date_to=${nextEnd.toISOString().split('T')[0]}`, { headers })
            .then(res => {
              cacheRef.current[nextCacheKey] = res.data || [];
              console.log(`🔮 Prefetched next week: ${nextCacheKey}`);
            })
            .catch(() => {}) // Silently fail for prefetch
        );
      }
      
      // Run prefetch in background without blocking
      if (fetchPromises.length > 0) {
        Promise.all(fetchPromises);
      }
    } catch (error) {
      // Silently fail - prefetch is not critical
    }
  };

  // Fetch exactly the schedules the Daily Timeline window needs (a few days),
  // independent of the weekly currentDate. A narrow range keeps the payload
  // small and complete (the wider fetch could exceed the backend cap and drop
  // shifts for large orgs).
  const fetchDailyViewSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        window.location.href = '/';
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const { start, end } = getDailyTimelineRange();
      const fromD = new Date(start); fromD.setDate(fromD.getDate() - 1);
      const toD = new Date(end); toD.setDate(toD.getDate() + 1);
      const fromStr = toLocalYMD(fromD);
      const toStr = toLocalYMD(toD);

      const res = await axios.get(`${API}/schedules?date_from=${fromStr}&date_to=${toStr}`, { headers });
      const raw = res.data || [];
      const seen = new Set();
      const uniq = [];
      raw.forEach(s => { if (!seen.has(s.id)) { seen.add(s.id); uniq.push(s); } });
      setAllSchedules(uniq);
    } catch (error) {
      console.error('Failed to load daily view schedules:', error);
      toast.error('Failed to load schedules');
    }
  };


  const loadScheduleRange = async (centerDate = null) => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use provided date or current date as the center point
      const baseDate = centerDate || currentDate;
      
      // Load 3 months: 1 month before and 2 months after the base date
      const startDate = new Date(baseDate);
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1); // First of month
      
      const endDate = new Date(baseDate);
      endDate.setMonth(endDate.getMonth() + 3); // Go to 3 months ahead
      endDate.setDate(0); // Then back to last day of previous month (= end of +2 months)
      
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];
      
      console.log(`📅 Loading schedule range: ${startStr} to ${endStr}`);
      
      const schedulesRes = await axios.get(
        `${API}/schedules?date_from=${startStr}&date_to=${endStr}`, 
        { headers }
      );
      
      const rawSchedules = schedulesRes.data || [];
      console.log(`📊 Loaded ${rawSchedules.length} schedules from API`);
      
      // CRITICAL FIX: Remove duplicates based on unique schedule ID
      // This prevents duplicate rendering when same schedule appears multiple times in API response
      const uniqueSchedules = [];
      const seenIds = new Set();
      
      rawSchedules.forEach(schedule => {
        if (!seenIds.has(schedule.id)) {
          seenIds.add(schedule.id);
          uniqueSchedules.push(schedule);
        } else {
          console.warn(`⚠️ Duplicate schedule ID found: ${schedule.id}`);
        }
      });
      
      console.log(`✅ After deduplication: ${uniqueSchedules.length} unique schedules`);
      
      setAllSchedules(uniqueSchedules);
      setLoadedDateRange({ start: startStr, end: endStr });
      
      // Filter for current week
      filterSchedulesForWeek(uniqueSchedules);
      
    } catch (error) {
      console.error('Failed to load schedule range:', error);
      toast.error('Failed to load schedules');
      setAllSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const filterSchedulesForWeek = (schedulesData = null) => {
    const data = schedulesData || allSchedules;
    const startOfWeek = getStartOfWeek(currentDate);
    const endOfWeek = getEndOfWeek(currentDate);
    
    // Filter cached schedules for the current week
    const weekSchedules = data.filter(schedule => {
      return schedule.date >= startOfWeek && schedule.date <= endOfWeek;
    });
    
    setSchedules(weekSchedules);
  };

  const fetchLunchPolicy = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/lunch-break-policy`, { headers });
      setLunchPolicy(res.data);
    } catch { /* non-critical */ }
  };


  const fetchEmployeesAndTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const [employeesRes, templatesRes, rolesRes, ptoTypesRes] = await Promise.all([
        axios.get(`${API}/employees`, { headers }),
        axios.get(`${API}/shift-templates`, { headers }),
        axios.get(`${API}/role-permissions?active_only=true`, { headers }),
        axios.get(`${API}/pto-types`, { headers })
      ]);
      
      // Filter to only show employees who have schedules (exclude view-only users)
      const scheduledEmployees = (employeesRes.data || []).filter(emp => emp.has_schedule !== false);
      setEmployees(scheduledEmployees);
      setTemplates(templatesRes.data || []);
      setAvailableRoles(rolesRes.data || []);
      
      // Filter only active PTO types
      const activePTOTypes = (ptoTypesRes.data || []).filter(type => type.is_active);
      setPtoTypes(activePTOTypes);
    } catch (error) {
      console.error('Failed to load employees/templates:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = getEndOfWeek(currentDate);
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        window.location.href = '/';
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const schedulesRes = await axios.get(
        `${API}/schedules?date_from=${startOfWeek}&date_to=${endOfWeek}`, 
        { headers }
      );
      
      const rawSchedules = schedulesRes.data || [];
      
      // Remove duplicates
      const uniqueSchedules = [];
      const seenIds = new Set();
      rawSchedules.forEach(schedule => {
        if (!seenIds.has(schedule.id)) {
          seenIds.add(schedule.id);
          uniqueSchedules.push(schedule);
        }
      });
      
      setSchedules(uniqueSchedules);
      
    } catch (error) {
      console.error('Failed to load schedules:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error('Session expired');
        window.location.href = '/';
      } else {
        toast.error('Failed to load schedules');
      }
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    // This is now used only for manual refresh
    await fetchEmployeesAndTemplates();
    await fetchSchedules();
  };

  const fetchPTORequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/pto-requests`, { headers });
      setPtoRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch PTO requests:', error);
    }
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay();
    const startOfWeek = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
    return startOfWeek.toISOString().split('T')[0];
  };

  const getEndOfWeek = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay();
    const endOfWeek = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day + 6);
    return endOfWeek.toISOString().split('T')[0];
  };

  // Helper function to get role display name
  const getRoleDisplayName = (roleId) => {
    const roleLabels = {
      'tier1': 'Tier 1',
      'tier2': 'Tier 2',
      'team_lead': 'Team Lead',
      'teamlead': 'Team Lead',
      'manager': 'Manager',
      'admin': 'Admin',
      'solution_engineer': 'Solution Engineer'
    };
    return roleLabels[roleId] || roleId.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper function to get role icon color
  const getRoleIconColor = (roleId) => {
    const colorMap = {
      'tier1': 'text-green-600',
      'tier2': 'text-blue-600',
      'team_lead': 'text-purple-600',
      'teamlead': 'text-purple-600',
      'manager': 'text-red-600',
      'admin': 'text-orange-600',
      'solution_engineer': 'text-teal-600'
    };
    return colorMap[roleId] || 'text-gray-600';
  };

  const getWeekDays = () => {
    const days = [];
    const startDateString = getStartOfWeek(currentDate);
    const [year, month, day] = startDateString.split('-').map(Number);
    const startDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
      days.push(dayDate);
    }
    return days;
  };

  const getSchedulesForDate = (date) => {
    const dateString = date.toISOString().split('T')[0];
    const daySchedules = schedules.filter(schedule => schedule.date === dateString);
    if (daySchedules.length > 0) {
      console.log(`✅ Found ${daySchedules.length} schedules for ${dateString}:`, daySchedules);
      daySchedules.forEach(s => console.log(`- ${s.employee?.name} at ${s.start_time}-${s.end_time}`));
    }
    return daySchedules;
  };

  // Daily Timeline View Helper Functions
  const getDailyTimelineRange = () => {
    // Live/"Today" mode: rolling 36h window centered on the current hour
    // (12h back → current hour → 24h ahead).
    if (!timelineAnchor) {
      const now = new Date();
      const currentHour = now.getHours();
      const start = new Date(now);
      start.setHours(currentHour - 12, 0, 0, 0);
      const end = new Date(now);
      end.setHours(currentHour + 24, 0, 0, 0);
      return { start, end };
    }
    // Specific selected day: 36h window from that day 00:00 through the next
    // day 12:00 — full 24h of the day plus the next morning so overnight
    // shifts can be seen end-to-end.
    const start = new Date(timelineAnchor);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + 36);
    return { start, end };
  };

  // Move the daily timeline forward/backward by whole days.
  const shiftTimelineDay = (delta) => {
    const base = timelineAnchor ? new Date(timelineAnchor) : new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + delta);
    setTimelineAnchor(base);
  };
  const goToTimelineToday = () => setTimelineAnchor(null);

  const generateHourlyTimeline = () => {
    const hours = [];
    const { start, end } = getDailyTimelineRange();
    
    // Generate hours from start to end (24 hours starting from current hour)
    const current = new Date(start);
    while (current <= end) {
      hours.push(new Date(current));
      current.setHours(current.getHours() + 1);
    }
    
    return hours;
  };

  const getSchedulesForDailyView = () => {
    const { start, end } = getDailyTimelineRange();
    
    // Get all unique dates covered by the timeline (LOCAL dates, not UTC)
    const dates = new Set();
    const current = new Date(start);
    while (current <= end) {
      dates.add(toLocalYMD(current));
      current.setDate(current.getDate() + 1);
    }
    
    // Also include previous day for cross-midnight shifts
    const dayBeforeStart = new Date(start);
    dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
    const prevDateStr = toLocalYMD(dayBeforeStart);
    
    // Get all schedules that could appear in the timeline
    const dailySchedules = allSchedules.filter(schedule => {
      // Include if date is in the timeline range
      if (dates.has(schedule.date)) {
        return true;
      }
      
      // Also include previous day's shifts that span midnight into our timeline
      if (schedule.date === prevDateStr) {
        const [startHour] = schedule.start_time.split(':').map(Number);
        const [endHour] = schedule.end_time.split(':').map(Number);
        // If end hour < start hour, shift spans midnight
        return endHour < startHour;
      }
      
      return false;
    });
    
    // Apply role filter
    let filteredSchedules = dailySchedules;
    if (dailyViewFilters.roleFilter !== 'all') {
      filteredSchedules = filteredSchedules.filter(s => s.role === dailyViewFilters.roleFilter);
    }
    
    // Apply shift type filter
    if (shiftTypeFilter !== 'all') {
      filteredSchedules = filteredSchedules.filter(s => {
        if (shiftTypeFilter === 'pto') return s.shift_type === 'pto' || s.shift_type === 'pending_pto';
        return s.shift_type === shiftTypeFilter;
      });
    }
    
    return filteredSchedules;
  };

  // Memoized daily-view derived data so the render doesn't recompute the
  // hourly timeline and full schedule filter once PER employee row (which was
  // O(employees × shifts) every render and slow for large orgs).
  const dailyHourlyTimeline = React.useMemo(
    () => (viewMode === 'daily' ? generateHourlyTimeline() : []),
    [viewMode, timelineAnchor]
  );
  const dailyViewSchedules = React.useMemo(
    () => (viewMode === 'daily' ? getSchedulesForDailyView() : []),
    [viewMode, timelineAnchor, allSchedules, shiftTypeFilter, dailyViewFilters]
  );
  const dailyByEmployee = React.useMemo(() => {
    const map = {};
    dailyViewSchedules.forEach((s) => {
      if (!map[s.user_id]) map[s.user_id] = [];
      map[s.user_id].push(s);
    });
    return map;
  }, [dailyViewSchedules]);

  // Location filter: an employee matches if any of their real (non-PTO/on-call) shifts
  // in the given scope are at the selected location. WFH override blocks are 'remote'.
  const employeeMatchesLocation = (employee, scopedSchedules) => {
    if (locationFilter === 'all') return true;
    return scopedSchedules.some(
      (s) =>
        s.user_id === employee.id &&
        s.shift_type !== 'pto' &&
        s.shift_type !== 'on_call' &&
        (s.work_location || 'office') === locationFilter
    );
  };



  const isShiftInHourSlot = (schedule, hour) => {
    const scheduleDate = new Date(schedule.date + 'T00:00:00');
    const [startHour, startMin] = schedule.start_time.split(':').map(Number);
    const [endHour, endMin] = schedule.end_time.split(':').map(Number);
    
    const shiftStart = new Date(scheduleDate);
    shiftStart.setHours(startHour, startMin, 0, 0);
    
    const shiftEnd = new Date(scheduleDate);
    shiftEnd.setHours(endHour, endMin, 0, 0);
    
    // Handle shifts that span midnight
    if (shiftEnd <= shiftStart) {
      shiftEnd.setDate(shiftEnd.getDate() + 1);
    }
    
    const hourEnd = new Date(hour);
    hourEnd.setHours(hour.getHours() + 1);
    
    return shiftStart < hourEnd && shiftEnd > hour;
  };

  const isHoliday = (date) => {
    let dateString;
    if (typeof date === 'string') {
      dateString = date;
    } else {
      // Format date as local date string (YYYY-MM-DD) to avoid timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dateString = `${year}-${month}-${day}`;
    }
    return holidays.find(h => h.date === dateString);
  };

  const getShiftTypeColor = (shiftType) => {
    const colorMap = {
      'regular': 'bg-blue-500',
      'on_call': 'bg-orange-500',
      'pto': 'bg-yellow-500',
      'sick': 'bg-red-500',
      'vacation': 'bg-purple-500',
      'wfh': 'bg-green-500'
    };
    return colorMap[shiftType] || 'bg-gray-500';
  };

  // Conflict detection and handling functions
  const checkForConflicts = async (userId, dates, shiftType = 'regular') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/schedules/check-conflicts`, {
        user_id: userId,
        dates: dates,
        shift_type: shiftType
      }, { headers });

      return response.data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return null;
    }
  };

  const handleConflictOverwrite = async () => {
    // Delete existing shifts on conflict dates, then create new ones
    if (!pendingScheduleData || !detectedConflicts) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Get all shift IDs to delete from duplicates
      const shiftIdsToDelete = [];
      detectedConflicts.duplicates?.forEach(dup => {
        dup.shifts.forEach(shift => {
          shiftIdsToDelete.push(shift.id);
        });
      });

      // Delete existing shifts
      if (shiftIdsToDelete.length > 0) {
        await axios.post(`${API}/schedules/bulk-delete`, shiftIdsToDelete, { headers });
      }

      // Create new schedules with override flags
      if (conflictResolutionMode === 'single') {
        await axios.post(`${API}/schedules?override_holiday=true&override_duplicate=true`, 
          pendingScheduleData, { headers });
      } else {
        // Multiple schedules
        for (const scheduleData of pendingScheduleData) {
          await axios.post(`${API}/schedules?override_holiday=true&override_duplicate=true`, 
            scheduleData, { headers });
        }
      }

      toast.success('Schedules created successfully (existing shifts overwritten)');
      setShowConflictModal(false);
      setPendingScheduleData(null);
      setDetectedConflicts(null);
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      setShowModal(false);
      setShowQuickSchedule(false);
      
      // Reset form and multi-day settings
      resetForm();
    } catch (error) {
      console.error('Error overwriting schedules:', error);
      toast.error('Failed to create schedules');
    }
  };

  const handleConflictAddAnyway = async () => {
    // Add shifts anyway, allowing duplicates and overriding holidays
    if (!pendingScheduleData) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (conflictResolutionMode === 'single') {
        await axios.post(`${API}/schedules?override_holiday=true&override_duplicate=true`, 
          pendingScheduleData, { headers });
      } else {
        // Multiple schedules
        for (const scheduleData of pendingScheduleData) {
          await axios.post(`${API}/schedules?override_holiday=true&override_duplicate=true`, 
            scheduleData, { headers });
        }
      }

      toast.success('Schedules created successfully');
      setShowConflictModal(false);
      setPendingScheduleData(null);
      setDetectedConflicts(null);
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      setShowModal(false);
      setShowQuickSchedule(false);
      
      // Reset form and multi-day settings
      resetForm();
    } catch (error) {
      console.error('Error creating schedules:', error);
      toast.error('Failed to create schedules');
    }
  };

  const handleConflictSkip = async () => {
    // Skip conflicting dates, only create on non-conflict dates
    if (!pendingScheduleData || !detectedConflicts) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Get dates to skip
      const datesToSkip = new Set();
      detectedConflicts.duplicates?.forEach(dup => datesToSkip.add(dup.date));
      detectedConflicts.holidays?.forEach(hol => datesToSkip.add(hol.date));

      // Filter out conflicting dates
      let schedulesToCreate = [];
      if (conflictResolutionMode === 'single') {
        if (!datesToSkip.has(pendingScheduleData.date)) {
          schedulesToCreate.push(pendingScheduleData);
        }
      } else {
        schedulesToCreate = pendingScheduleData.filter(s => !datesToSkip.has(s.date));
      }

      // Create non-conflicting schedules
      for (const scheduleData of schedulesToCreate) {
        await axios.post(`${API}/schedules`, scheduleData, { headers });
      }

      const skippedCount = (Array.isArray(pendingScheduleData) ? pendingScheduleData.length : 1) - schedulesToCreate.length;
      toast.success(`Created ${schedulesToCreate.length} schedule(s), skipped ${skippedCount} conflict(s)`);
      
      setShowConflictModal(false);
      setPendingScheduleData(null);
      setDetectedConflicts(null);
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      setShowModal(false);
      setShowQuickSchedule(false);
      
      // Reset form and multi-day settings
      resetForm();
    } catch (error) {
      console.error('Error creating schedules:', error);
      toast.error('Failed to create schedules');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (loading || submitting) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found - redirecting to login');
        toast.error('Authentication required - please login again');
        window.location.href = '/';
        setSubmitting(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingSchedule) {
        // Update existing schedule - check if duplicating to multiple days
        if (enableMultipleDays && selectedDaysOfWeek.length > 0 && scheduleStartDate) {
          // Generate dates based on days of week, start date, and duration mode
          const dates = [];
          // Parse date as local time to avoid timezone issues
          const [year, month, day] = scheduleStartDate.split('-').map(Number);
          const startDate = new Date(year, month - 1, day);
          
          // Calculate end date based on mode
          let endDate;
          if (scheduleDurationMode === 'endDate' && scheduleEndDate) {
            const [eYear, eMonth, eDay] = scheduleEndDate.split('-').map(Number);
            endDate = new Date(eYear, eMonth - 1, eDay);
          } else {
            // Calculate from weeks
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + (scheduleWeeksCount * 7));
          }
          
          // Generate all matching dates
          let currentWeek = 0;
          const maxWeeks = 260; // Safety limit: 5 years
          
          while (currentWeek < maxWeeks) {
            let foundDateInWeek = false;
            
            for (let dayOfWeek of selectedDaysOfWeek) {
              // Start from the beginning of the week containing startDate
              const weekStart = new Date(startDate);
              weekStart.setDate(startDate.getDate() - startDate.getDay() + (currentWeek * 7));
              
              // Add days to get to the desired day of week
              const date = new Date(weekStart);
              date.setDate(weekStart.getDate() + dayOfWeek);
              
              // Only include dates on or after start date
              if (date < startDate) {
                continue;
              }
              
              // Stop if we've exceeded the end date (inclusive)
              if (date > endDate) {
                break;
              }
              
              foundDateInWeek = true;
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              dates.push(`${year}-${month}-${day}`);
            }
            
            // If no dates were added this week and we're past the start, we're done
            if (!foundDateInWeek && currentWeek > 0) {
              break;
            }
            
            currentWeek++;
          }
          
          // Create copies for multiple days - CHECK FOR CONFLICTS FIRST
          const conflicts = await checkForConflicts(formData.user_id, dates, formData.shift_type);
          
          // Only block on holidays for regular shifts (not on-call)
          if (conflicts && (conflicts.duplicates?.length > 0 || (conflicts.holidays?.length > 0 && formData.shift_type !== 'on_call'))) {
            // Store pending schedules and show conflict modal
            const schedulesToCreate = dates.map(date => ({ ...formData, date }));
            setPendingScheduleData(schedulesToCreate);
            setDetectedConflicts(conflicts);
            setConflictResolutionMode('multiple');
            setShowConflictModal(true);
            setSubmitting(false);
            return; // Wait for user decision
          }
          
          // No conflicts, proceed
          const promises = dates.map(date => {
            const scheduleData = { ...formData, date };
            return axios.post(`${API}/schedules`, scheduleData, { headers });
          });
          await Promise.all(promises);
          toast.success(`Schedule duplicated to ${dates.length} days`);
        } else {
          await axios.put(`${API}/schedules/${editingSchedule.id}`, formData, { headers });
          toast.success('Schedule updated successfully');
        }
      } else {
        // Create new schedule(s)
        if (enableMultipleDays && selectedDaysOfWeek.length > 0 && scheduleStartDate) {
          // Generate dates based on days of week, start date, and duration mode
          const dates = [];
          // Parse date as local time to avoid timezone issues
          const [year, month, day] = scheduleStartDate.split('-').map(Number);
          const startDate = new Date(year, month - 1, day);
          
          // Calculate end date based on mode
          let endDate;
          if (scheduleDurationMode === 'endDate' && scheduleEndDate) {
            const [eYear, eMonth, eDay] = scheduleEndDate.split('-').map(Number);
            endDate = new Date(eYear, eMonth - 1, eDay);
          } else {
            // Calculate from weeks
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + (scheduleWeeksCount * 7));
          }
          
          // Generate all matching dates
          let currentWeek = 0;
          const maxWeeks = 260; // Safety limit: 5 years
          
          while (currentWeek < maxWeeks) {
            let foundDateInWeek = false;
            
            for (let dayOfWeek of selectedDaysOfWeek) {
              // Start from the beginning of the week containing startDate
              const weekStart = new Date(startDate);
              weekStart.setDate(startDate.getDate() - startDate.getDay() + (currentWeek * 7));
              
              // Add days to get to the desired day of week
              const date = new Date(weekStart);
              date.setDate(weekStart.getDate() + dayOfWeek);
              
              // Only include dates on or after start date
              if (date < startDate) {
                continue;
              }
              
              // Stop if we've exceeded the end date (inclusive)
              if (date > endDate) {
                break;
              }
              
              foundDateInWeek = true;
              
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              dates.push(`${year}-${month}-${day}`);
            }
            
            // If no dates were added this week and we're past the start, we're done
            if (!foundDateInWeek && currentWeek > 0) {
              break;
            }
            
            currentWeek++;
          }
          
          // Create for multiple days - CHECK FOR CONFLICTS FIRST
          const conflicts = await checkForConflicts(formData.user_id, dates, formData.shift_type);
          
          // Only block on holidays for regular shifts (not on-call)
          if (conflicts && (conflicts.duplicates?.length > 0 || (conflicts.holidays?.length > 0 && formData.shift_type !== 'on_call'))) {
            // Store pending schedules and show conflict modal
            const schedulesToCreate = dates.map(date => ({ ...formData, date }));
            setPendingScheduleData(schedulesToCreate);
            setDetectedConflicts(conflicts);
            setConflictResolutionMode('multiple');
            setShowConflictModal(true);
            setSubmitting(false);
            return; // Wait for user decision
          }
          
          // No conflicts, proceed with creation
          const promises = dates.map(date => {
            const scheduleData = { ...formData, date };
            return axios.post(`${API}/schedules`, scheduleData, { headers });
          });
          await Promise.all(promises);
          toast.success(`Schedules created for ${dates.length} days`);
        } else {
          // Create single schedule - CHECK FOR CONFLICTS FIRST
          const conflicts = await checkForConflicts(formData.user_id, [formData.date], formData.shift_type);
          
          // Only block on holidays for regular shifts (not on-call)
          if (conflicts && (conflicts.duplicates?.length > 0 || (conflicts.holidays?.length > 0 && formData.shift_type !== 'on_call'))) {
            // Store pending schedule and show conflict modal
            setPendingScheduleData(formData);
            setDetectedConflicts(conflicts);
            setConflictResolutionMode('single');
            setShowConflictModal(true);
            setSubmitting(false);
            return; // Wait for user decision
          }
          
          // No conflicts, proceed with creation
          await axios.post(`${API}/schedules`, formData, { headers });
          toast.success('Schedule created successfully');
        }
      }
      
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      resetForm();
      setEditingSchedule(null);
      setShowModal(false);
      setEnableMultipleDays(false);
      setSelectedDaysOfWeek([]);
      setScheduleStartDate('');
      setScheduleWeeksCount(1);
      setScheduleDurationMode('weeks');
      setScheduleEndDate('');
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to ${editingSchedule ? 'update' : 'create'} schedule`;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (weekLocked) {
      toast.error('Schedule is locked - cannot delete shifts');
      return;
    }
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        await axios.delete(`${API}/schedules/${scheduleId}`);
        toast.success('Schedule deleted successfully');
        await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete schedule');
      }
    }
  };

  const handleRequestSwap = async () => {
    if (!scheduleToSwap) {
      toast.error('No shift selected for swap');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${API}/swap-requests`,
        {
          schedule_id: scheduleToSwap.id,
          reason: swapReason,
          requires_approval: true // Always require manager approval for security
        },
        { headers }
      );

      toast.success('Swap request created successfully');
      setShowSwapModal(false);
      setScheduleToSwap(null);
      setSwapReason('');
    } catch (error) {
      console.error('Failed to create swap request:', error);
      toast.error(error.response?.data?.detail || 'Failed to create swap request');
    }
  };

  const handleTemplateChange = (templateId) => {
    if (templateId === 'custom' || templateId === '') {
      // Custom shift - no template, set default values
      setFormData({
        ...formData,
        shift_template_id: '',
        start_time: '09:00',
        end_time: '17:00',
        shift_type: 'regular'
      });
    } else {
      // Template selected - populate from template
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setFormData({
          ...formData,
          shift_template_id: templateId,
          start_time: template.start_time,
          end_time: template.end_time,
          shift_type: template.shift_type
        });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      shift_template_id: '',
      date: '',
      start_time: '',
      end_time: '',
      shift_type: 'regular',
      role: 'tier1',
      work_location: 'office',
      notes: ''
    });
    setEditingSchedule(null);
    setEnableMultipleDays(false);
    setSelectedDaysOfWeek([]);
    setScheduleStartDate('');
    setScheduleWeeksCount(1);
  };

  const resetQuickScheduleForm = () => {
    setQuickScheduleData({
      user_id: '',
      shift_template_id: '',
      days: [],
      notes: ''
    });
  };

  const handleQuickScheduleSubmit = async (e) => {
    e.preventDefault();
    
    if (quickScheduleData.days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    try {
      const template = templates.find(t => t.id === quickScheduleData.shift_template_id);
      if (!template) {
        toast.error('Shift template not found');
        return;
      }

      // Create schedules for selected days
      const promises = quickScheduleData.days.map(dayDate => {
        const scheduleData = {
          user_id: quickScheduleData.user_id,
          shift_template_id: quickScheduleData.shift_template_id,
          date: dayDate,
          start_time: template.start_time,
          end_time: template.end_time,
          shift_type: template.shift_type,
          notes: quickScheduleData.notes
        };
        return axios.post(`${API}/schedules`, scheduleData);
      });

      await Promise.all(promises);
      toast.success(`Created ${quickScheduleData.days.length} schedules successfully`);
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      resetQuickScheduleForm();
      setShowQuickSchedule(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create schedules';
      toast.error(message);
    }
  };

  const toggleDay = (dayDate) => {
    const newDays = quickScheduleData.days.includes(dayDate)
      ? quickScheduleData.days.filter(d => d !== dayDate)
      : [...quickScheduleData.days, dayDate];
    setQuickScheduleData({ ...quickScheduleData, days: newDays });
  };

  const handlePTOSubmit = async (e) => {
    e.preventDefault();
    if (ptoSubmitting) return;
    setPtoSubmitting(true);
    
    try {
      let targetEmployee;
      
      if (selectedEmployeeForPTO) {
        // Manager assignment - use selected employee
        targetEmployee = selectedEmployeeForPTO;
      } else {
        // Self-request - find current user's employee record by email
        targetEmployee = employees.find(emp => emp.email === user?.email);
        
        // If not found in employees, fetch from users endpoint
        if (!targetEmployee) {
          try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const usersResponse = await axios.get(`${API}/users`, { headers });
            const allUsers = usersResponse.data || [];
            targetEmployee = allUsers.find(u => u.email === user?.email);
          } catch (err) {
            console.error('Failed to fetch users:', err);
          }
        }
        
        if (!targetEmployee) {
          toast.error('Unable to find your user record. Please contact an administrator.');
          return;
        }
      }
      
      // Create PTO request with target employee ID
      const ptoData = {
        ...ptoForm,
        user_id: targetEmployee.id
      };
      
      let token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found - redirecting to login');
        toast.error('Authentication required - please login again');
        window.location.href = '/';
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create the PTO request
      const ptoResponse = await axios.post(`${API}/pto-requests`, ptoData, { headers });
      const createdPTO = ptoResponse.data;
      
      // If manager is creating it, auto-approve and create schedules
      if (selectedEmployeeForPTO && isManagerOrAdmin) {
        // Auto-approve the PTO request
        await axios.put(`${API}/pto-requests/${createdPTO.id}/approve`, {}, { headers });
        
        toast.success(`PTO assigned to ${selectedEmployeeForPTO.name} and added to schedule`);
      } else {
        toast.success('PTO request submitted successfully');
      }
      
      fetchPTORequests();
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      resetPTOForm();
      setSelectedEmployeeForPTO(null);
      setShowPTOModal(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit PTO request';
      toast.error(message);
    } finally {
      setPtoSubmitting(false);
    }
  };

  const resetPTOForm = () => {
    setPtoForm({
      start_date: '',
      end_date: '',
      pto_type: 'pto',
      reason: '',
      is_partial_day: false,
      start_time: '09:00',
      end_time: '17:00',
      worked_start_time: '',
      worked_end_time: '',
      exclude_weekends: true
    });
    setSelectedEmployeeForPTO(null);
  };

  const handleWFHSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get current user info from auth context
      if (!user || !user.email) {
        toast.error('User session not found. Please login again.');
        return;
      }
      
      console.log('WFH Request - Current user email:', user.email);
      
      // Try to find user ID - try multiple sources
      let userId = null;
      
      // Manager assignment mode: use the selected employee
      if (selectedEmployeeForWFH) {
        userId = selectedEmployeeForWFH.id;
      }
      
      // First: Check if user object already has ID
      if (!userId && user.id) {
        userId = user.id;
        console.log('Found user ID from auth context:', userId);
      }
      
      // Second: Try employees list
      if (!userId) {
        const employee = employees.find(emp => emp.email === user.email);
        if (employee) {
          userId = employee.id;
          console.log('Found user ID from employees list:', userId);
        }
      }
      
      // Third: Fetch from /api/users as fallback
      if (!userId) {
        console.log('Attempting fallback to /api/users endpoint...');
        try {
          const usersResponse = await axios.get(`${API}/users`, { headers });
          const allUsers = usersResponse.data || [];
          const foundUser = allUsers.find(u => u.email === user.email);
          
          if (foundUser) {
            userId = foundUser.id;
            console.log('Found user ID via fallback:', userId);
          }
        } catch (err) {
          console.error('Failed to fetch users:', err);
        }
      }
      
      // If still no user ID found, error out
      if (!userId) {
        console.error('Could not find user ID for email:', user.email);
        toast.error(`Unable to find your user record. Please contact an administrator. (Email: ${user.email})`);
        return;
      }
      
      // Validate WFH type is selected
      if (!wfhForm.wfh_type) {
        toast.error('Please select a WFH type');
        return;
      }
      
      console.log('Submitting WFH request with user ID:', userId);
      
      const wfhType = wfhForm.wfh_type;
      const wfhHours = wfhForm.hours || 8;
      const historicalParam = isHistoricalWFH ? '&is_historical=true' : '';
      const periodParam = wfhHours === 4 ? `&wfh_period=${wfhForm.wfh_period || 'morning'}` : '';
      // Send specific selected dates so backend only counts those (not the full range)
      const selectedDatesParam = selectedWFHShifts.length > 0 
        ? `&selected_dates=${selectedWFHShifts.map(s => s.date).sort().join(',')}` 
        : '';
      const createResp = await axios.post(
        `${API}/wfh-requests?start_date=${wfhForm.start_date}&end_date=${wfhForm.end_date}&user_id=${userId}&reason=${encodeURIComponent(wfhForm.reason || '')}&wfh_type=${wfhType}&hours=${wfhHours}${periodParam}${historicalParam}${selectedDatesParam}`,
        {},
        { headers }
      );

      // Manager assignment: auto-approve so it applies to the schedule immediately
      if (selectedEmployeeForWFH && createResp.data?.id && !isHistoricalWFH) {
        await axios.put(`${API}/wfh-requests/${createResp.data.id}/approve`, {}, { headers });
      }
      
      const typeLabel = wfhTypes.find(t => t.value === wfhType)?.display_name || wfhType;
      if (selectedEmployeeForWFH) {
        toast.success(`${typeLabel} WFH assigned to ${selectedEmployeeForWFH.name}`);
      } else {
        toast.success(`${typeLabel} WFH request submitted successfully`);
      }
      setShowWFHModal(false);
      setSelectedEmployeeForWFH(null);
      setWfhForm({ start_date: '', end_date: '', reason: '', hours: 8, wfh_type: '', wfh_period: 'morning' });
      await fetchSchedulesForCurrentWeek(true, true);
    } catch (error) {
      console.error('Failed to submit WFH request:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to submit WFH request');
    }
  };

  const resetWFHForm = () => {
    setWfhForm({
      start_date: '',
      end_date: '',
      reason: '',
      hours: 8,
      wfh_type: ''
    });
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Build the list of dates (single or multi-day)
      const dates = [activityForm.date];
      if (activityForm.end_date && activityForm.end_date > activityForm.date) {
        let d = new Date(activityForm.date + 'T00:00:00');
        const end = new Date(activityForm.end_date + 'T00:00:00');
        d.setDate(d.getDate() + 1); // start date already added
        while (d <= end) {
          dates.push(d.toISOString().split('T')[0]);
          d.setDate(d.getDate() + 1);
        }
      }

      const payload = {
        ...activityForm,
        start_time: activityForm.all_day ? '00:00' : activityForm.start_time,
        end_time: activityForm.all_day ? '23:59' : activityForm.end_time,
      };
      
      if (editingActivity) {
        await axios.put(`${API}/activities/${editingActivity.id}`, payload, { headers });
        // If multi-day edit, create additional days
        if (dates.length > 1) {
          for (const dt of dates.slice(1)) {
            const dayPayload = { ...payload, date: dt };
            if (payload.user_ids && payload.user_ids.length > 0) {
              await axios.post(`${API}/activities/bulk`, { ...dayPayload, user_ids: payload.user_ids }, { headers });
            } else {
              await axios.post(`${API}/activities`, dayPayload, { headers });
            }
          }
          toast.success(`Activity updated and extended to ${dates.length} days`);
        } else {
          toast.success('Activity updated successfully');
        }
      } else {
        // Create for each date
        for (const dt of dates) {
          const dayPayload = { ...payload, date: dt };
          if (activityForm.user_ids && activityForm.user_ids.length > 0) {
            await axios.post(`${API}/activities/bulk`, {
              user_ids: activityForm.user_ids,
              date: dt,
              start_time: dayPayload.start_time,
              end_time: dayPayload.end_time,
              activity_type: activityForm.activity_type,
              title: activityForm.title,
              description: activityForm.description,
              location: activityForm.location,
              client_name: activityForm.client_name
            }, { headers });
          } else {
            await axios.post(`${API}/activities`, dayPayload, { headers });
          }
        }
        const msg = dates.length > 1
          ? `Activity created for ${dates.length} days${activityForm.user_ids?.length > 0 ? ` x ${activityForm.user_ids.length} people` : ''}`
          : activityForm.user_ids?.length > 0
            ? `Activity created for ${activityForm.user_ids.length} people`
            : 'Activity created successfully';
        toast.success(msg);
      }
      
      // Refresh activities
      const weekStartStr = getStartOfWeek(currentDate);
      const [year, month, day] = weekStartStr.split('-').map(Number);
      const weekStart = new Date(year, month - 1, day);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const startStr = weekStart.toISOString().split('T')[0];
      const endStr = weekEnd.toISOString().split('T')[0];
      await fetchActivities(startStr, endStr);
      
      setShowActivityModal(false);
      setEditingActivity(null);
      setActivityForm({
        user_id: '',
        user_ids: [],
        date: '',
        end_date: '',
        start_time: '09:00',
        end_time: '10:00',
        all_day: false,
        activity_type: '',
        title: '',
        description: '',
        location: '',
        client_name: ''
      });
    } catch (error) {
      console.error('Failed to save activity:', error);
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/activities/${activityId}`, { headers });
      toast.success('Activity deleted successfully');
      
      // Refresh activities
      const weekStartStr = getStartOfWeek(currentDate);
      const [year, month, day] = weekStartStr.split('-').map(Number);
      const weekStart = new Date(year, month - 1, day);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const startStr = weekStart.toISOString().split('T')[0];
      const endStr = weekEnd.toISOString().split('T')[0];
      await fetchActivities(startStr, endStr);
      
      setShowActivityModal(false);
    } catch (error) {
      console.error('Failed to delete activity:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const fetchUpcomingShifts = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/users/${userId}/upcoming-shifts?days_ahead=${WFH_QUICK_LIST_DAYS}`, { headers });
      setUpcomingShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Failed to fetch upcoming shifts:', error);
      setUpcomingShifts([]);
    }
  };

  const toggleWFHShift = (shift) => {
    if (!shift) return;
    const exists = selectedWFHShifts.some(s => s.date === shift.date);
    const newSelection = exists
      ? selectedWFHShifts.filter(s => s.date !== shift.date)
      : [...selectedWFHShifts, shift];
    setSelectedWFHShifts(newSelection);
    const dates = newSelection.map(s => s.date).sort();
    setWfhForm({ ...wfhForm, start_date: dates[0] || '', end_date: dates[dates.length - 1] || '' });
  };
  
  const fetchFlexBankInfo = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/users/${userId}/flex-bank`, { headers });
      setFlexBankInfo(response.data);
      
      // Also fetch system settings for allowed hours
      try {
        const settingsResponse = await axios.get(`${API}/system-settings`, { headers });
        if (settingsResponse.data?.flex_bank_allowed_hours) {
          setFlexBankSettings({ allowed_hours: settingsResponse.data.flex_bank_allowed_hours });
        }
      } catch (e) {
        // Use default if settings not available
      }
    } catch (error) {
      console.error('Failed to fetch Flex Bank info:', error);
      setFlexBankInfo(null);
    }
  };

  const openWFHModal = async () => {
    // Get user ID first
    let userId = null;
    if (user.id) {
      userId = user.id;
    } else {
      const employee = employees.find(emp => emp.email === user.email);
      if (employee) userId = employee.id;
    }
    
    if (userId) {
      setWfhUserId(userId);
      await fetchUpcomingShifts(userId);
      await fetchFlexBankInfo(userId);
    }
    
    // Reset selections when opening modal
    setSelectedWFHShifts([]);
    setWfhForm({ start_date: '', end_date: '', reason: '', hours: 8, wfh_type: '' });
    setIsHistoricalWFH(false);
    setShowWFHModal(true);
  };

  // Manager: open the WFH modal to ASSIGN work-from-home for a specific employee/day
  const openWFHModalForEmployee = async (employee, day) => {
    setSelectedEmployeeForWFH(employee);
    const dayStr = day.toISOString().split('T')[0];
    await fetchUpcomingShifts(employee.id);
    await fetchFlexBankInfo(employee.id);
    setSelectedWFHShifts([]);
    setWfhForm({ start_date: dayStr, end_date: dayStr, reason: '', hours: 8, wfh_type: '', wfh_period: 'morning' });
    setIsHistoricalWFH(false);
    setShowWFHModal(true);
  };

  const approvePTO = async (ptoId) => {
    try {
      await axios.put(`${API}/pto-requests/${ptoId}/approve`);
      toast.success('PTO request approved');
      fetchPTORequests();
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
    } catch (error) {
      toast.error('Failed to approve PTO request');
    }
  };

  const denyPTO = async (ptoId) => {
    try {
      await axios.put(`${API}/pto-requests/${ptoId}/deny`);
      toast.success('PTO request denied');
      fetchPTORequests();
    } catch (error) {
      toast.error('Failed to deny PTO request');
    }
  };

  const handleScheduleDelete = async (scheduleId, isPTO = false, reason = '') => {
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found - redirecting to login');
        toast.error('Authentication required - please login again');
        window.location.href = '/';
        return;
      }
      
      await axios.delete(`${API}/schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: reason.trim() ? { reason: reason.trim() } : {}
      });
      toast.success(isPTO ? 'PTO removed from schedule successfully' : 'Schedule deleted successfully');
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(isPTO ? 'Failed to remove PTO' : 'Failed to delete schedule');
    }
  };

  // Default the PTO type to a real configured type (the 'pto' placeholder isn't a valid id)
  useEffect(() => {
    if (showPTOModal && ptoTypes.length && !ptoTypes.some(t => t.id === ptoForm.pto_type)) {
      const first = ptoTypes.find(t => t.id);
      if (first) setPtoForm(prev => ({ ...prev, pto_type: first.id }));
    }
  }, [showPTOModal, ptoTypes, ptoForm.pto_type]);

  // Load the linked PTO request when a PTO block is opened in the edit modal
  useEffect(() => {
    const loadPto = async () => {
      setPtoDetailEdit(null);
      setPtoRemoveReason('');
      if (showModal && isPtoBlockOpen && editingSchedule?.pto_request_id) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/pto-requests`, {
            params: { employee_id: editingSchedule.user_id },
            headers: { Authorization: `Bearer ${token}` }
          });
          const list = Array.isArray(res.data) ? res.data : (res.data.requests || []);
          setEditingPtoRequest(list.find(r => r.id === editingSchedule.pto_request_id) || null);
        } catch {
          setEditingPtoRequest(null);
        }
      } else {
        setEditingPtoRequest(null);
      }
    };
    loadPto();
  }, [showModal, editingSchedule]);

  const handleRemovePTOFromModal = async () => {
    if (!editingSchedule) return;
    await handleScheduleDelete(editingSchedule.id, true, ptoRemoveReason);
    setShowModal(false);
    setEditingSchedule(null);
  };

  const handleCancelPendingPTO = async () => {
    if (!editingSchedule?.pto_request_id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pto-requests/${editingSchedule.pto_request_id}/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: ptoRemoveReason.trim() ? { reason: ptoRemoveReason.trim() } : {}
      });
      toast.success('Time off request cancelled');
      setShowModal(false);
      setEditingSchedule(null);
      fetchPTORequests();
      await fetchSchedulesForCurrentWeek(true, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleApprovePendingPTO = async () => {
    if (!editingSchedule?.pto_request_id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pto-requests/${editingSchedule.pto_request_id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Time off approved and added to the schedule');
      setShowModal(false);
      setEditingSchedule(null);
      fetchPTORequests();
      await fetchSchedulesForCurrentWeek(true, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleSavePTODetails = async () => {
    if (!editingPtoRequest || !ptoDetailEdit || ptoDetailSaving) return;
    setPtoDetailSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/pto-requests/${editingPtoRequest.id}/details`, ptoDetailEdit, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = res.data.pto_request || {};
      const typeObj = ptoTypes.find(t => t.id === updated.pto_type || t.value === updated.pto_type) || {};
      setEditingPtoRequest({ ...editingPtoRequest, ...updated, pto_type_display: typeObj.display_name || typeObj.name || editingPtoRequest.pto_type_display });
      setEditingSchedule({ ...editingSchedule, role: updated.pto_type, notes: `PTO: ${updated.pto_type} - ${updated.reason || ''}` });
      setPtoDetailEdit(null);
      toast.success('Time off updated');
      await fetchSchedulesForCurrentWeek(true, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update time off');
    } finally {
      setPtoDetailSaving(false);
    }
  };

  const getScheduleDisplayName = (schedule) => {
    const type = schedule.shift_type === 'pto' ? 'PTO' : 
                schedule.shift_type === 'on_call' ? 'On-Call shift' : 'shift';
    return `${schedule.employee?.name}'s ${type}`;
  };

  const handleClearEmployeeSchedule = async () => {
    if (!employeeToClear) return;
    
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      let schedulesToDelete = [];
      
      if (clearScheduleRange === 'week') {
        // Clear current week only - use local date formatting
        const weekDates = weekDays.map(d => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        });
        schedulesToDelete = schedules.filter(s => 
          s.user_id === employeeToClear.id && weekDates.includes(s.date)
        );
      } else if (clearScheduleRange === 'all') {
        // Fetch ALL schedules for this employee from backend with broader date range
        // Use 2-year range (1 year past, 1 year future) to cover most schedules
        const today = new Date();
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const oneYearFromNow = new Date(today);
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        
        const startDate = `${oneYearAgo.getFullYear()}-01-01`;
        const endDate = `${oneYearFromNow.getFullYear()}-12-31`;
        
        const response = await axios.get(`${API}/schedules?date_from=${startDate}&date_to=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('🔍 Fetched schedules:', response.data.length);
        console.log('🔍 Looking for user_id:', employeeToClear.id);
        console.log('🔍 Sample schedule user_ids:', response.data.slice(0, 5).map(s => s.user_id));
        
        schedulesToDelete = response.data.filter(s => s.user_id === employeeToClear.id);
        
        console.log('🔍 Matched schedules:', schedulesToDelete.length);
      } else if (clearScheduleRange === 'custom') {
        // Fetch schedules in date range from backend
        const response = await axios.get(`${API}/schedules?date_from=${clearScheduleDateRange.start}&date_to=${clearScheduleDateRange.end}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        schedulesToDelete = response.data.filter(s => s.user_id === employeeToClear.id);
      }
      
      // Apply shift type filter
      if (clearScheduleShiftType !== 'all') {
        schedulesToDelete = schedulesToDelete.filter(s => {
          if (clearScheduleShiftType === 'regular') {
            return s.shift_type !== 'on_call';
          } else if (clearScheduleShiftType === 'on_call') {
            return s.shift_type === 'on_call';
          }
          return true;
        });
      }

      if (schedulesToDelete.length === 0) {
        toast.info('No schedules to delete in selected range');
        setShowClearScheduleModal(false);
        return;
      }

      // Use bulk delete endpoint for better performance
      const scheduleIds = schedulesToDelete.map(s => s.id);
      
      const response = await axios.post(
        `${API}/schedules/bulk-delete`,
        scheduleIds,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const deletedCount = response.data.deleted_count || schedulesToDelete.length;
      const cancelledPTO = response.data.cancelled_pto_requests || 0;
      
      let successMessage = `Cleared ${deletedCount} schedule(s) for ${employeeToClear.name}`;
      if (cancelledPTO > 0) {
        successMessage += ` (${cancelledPTO} PTO request(s) cancelled)`;
      }
      
      toast.success(successMessage);
      await fetchSchedulesForCurrentWeek(true, true); // Force refresh and preserve scroll
      setShowClearScheduleModal(false);
      setEmployeeToClear(null);
      setClearScheduleRange('week');
      setClearScheduleShiftType('all');
      setClearScheduleDateRange({ start: '', end: '' });
    } catch (error) {
      console.error('Clear schedule failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear schedule');
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateShiftDuration = (startTime, endTime, includeLunch = false, shiftType = 'regular') => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    
    // Handle overnight shifts
    let totalMinutes;
    if (end < start) {
      const nextDay = new Date(end);
      nextDay.setDate(nextDay.getDate() + 1);
      totalMinutes = (nextDay - start) / (1000 * 60);
    } else {
      totalMinutes = (end - start) / (1000 * 60);
    }
    
    // If this is a working time calculation and shift needs lunch, subtract lunch time
    // On-call shifts never get lunch breaks
    if (!includeLunch && totalMinutes > 6 * 60 && shiftType !== 'on_call') { // More than 6 hours
      const lunchDuration = (lunchPolicy?.feature_enabled && lunchPolicy?.lunch_enabled)
        ? (lunchPolicy.lunch_duration || 0)
        : 0;
      totalMinutes -= lunchDuration;
    }
    
    return totalMinutes / 60; // Convert to hours
  };

  const getWorkingHours = (startTime, endTime, shiftType = 'regular') => {
    return calculateShiftDuration(startTime, endTime, false, shiftType); // Excludes lunch
  };

  const getTotalShiftTime = (startTime, endTime, shiftType = 'regular') => {
    return calculateShiftDuration(startTime, endTime, true, shiftType); // Includes lunch (but on-call won't have lunch anyway)
  };

  const needsLunchBreak = (startTime, endTime, shiftType = 'regular') => {
    // On-call shifts never get lunch breaks
    if (shiftType === 'on_call') {
      return false;
    }
    const totalDuration = getTotalShiftTime(startTime, endTime, shiftType);
    return totalDuration > 6; // Lunch required for shifts over 6 hours
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // Returns true if two HH:MM time ranges overlap (conservatively treats overnight/unknown as overlapping)
  const timeRangesOverlap = (aStart, aEnd, bStart, bEnd) => {
    if (!aStart || !aEnd || !bStart || !bEnd) return true;
    if (aEnd <= aStart || bEnd <= bStart) return true; // overnight/zero-length — be safe
    return aStart < bEnd && bStart < aEnd;
  };

  // Creates the planned copy shifts, resolving overlaps per the chosen strategy:
  //  - 'keep_both' : create the new shift alongside existing (stacked)
  //  - 'replace'   : delete overlapping existing shift(s), then create the new one
  //  - 'skip'      : do not create shifts that overlap an existing one
  const performCopy = async (plan, resolution, ctx) => {
    const { headers, modeText } = ctx;
    let successCount = 0, failCount = 0, skipCount = 0, replacedCount = 0;
    const q = '?override_duplicate=true&override_holiday=true';
    toast.info(`Copying ${plan.length} schedule(s)...`);
    for (const item of plan) {
      const { newSchedule, overlaps } = item;
      try {
        if (overlaps && overlaps.length > 0) {
          if (resolution === 'skip') { skipCount++; continue; }
          if (resolution === 'replace') {
            for (const ex of overlaps) {
              await axios.delete(`${API}/schedules/${ex.id}`, { headers });
              replacedCount++;
            }
          }
        }
        await axios.post(`${API}/schedules${q}`, newSchedule, { headers });
        successCount++;
      } catch (createError) {
        failCount++;
        console.warn('Failed to copy a schedule:', createError.response?.data?.detail || createError);
      }
    }
    let msg = `Copied ${successCount} ${modeText}`;
    if (replacedCount > 0) msg += `, replaced ${replacedCount}`;
    if (skipCount > 0) msg += `, skipped ${skipCount} overlapping`;
    if (failCount > 0) msg += `, ${failCount} failed`;
    if (failCount === 0) toast.success(msg); else toast.warning(msg);

    await fetchSchedulesForCurrentWeek(true, true);
    setShowCopyWeekModal(false);
    setCopyOverlap({ open: false, plan: [], conflicts: [], headers: null, modeText: '', expanded: false });
    setCopyWeekData({ sourceWeek: '', targetWeek: '', overwriteExisting: false, copyMode: 'entire', selectedEmployees: [], selectedDays: [] });
  };

  const resolveCopyOverlap = async (resolution) => {
    const { plan, headers, modeText } = copyOverlap;
    setCopyOverlap({ ...copyOverlap, open: false });
    await performCopy(plan, resolution, { headers, modeText });
  };

  const handleCopyWeek = async () => {
    if (!copyWeekData.sourceWeek || !copyWeekData.targetWeek) {
      toast.error('Please select both source and target weeks');
      return;
    }

    // Validate selections based on copy mode
    if (copyWeekData.copyMode === 'employees' && copyWeekData.selectedEmployees.length === 0) {
      toast.error('Please select at least one employee to copy');
      return;
    }

    if (copyWeekData.copyMode === 'days' && copyWeekData.selectedDays.length === 0) {
      toast.error('Please select at least one day to copy');
      return;
    }

    try {
      let token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found - redirecting to login');
        toast.error('Authentication required - please login again');
        window.location.href = '/';
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Get source week schedules
      const sourceStartDate = new Date(copyWeekData.sourceWeek);
      const sourceEndDate = new Date(sourceStartDate);
      sourceEndDate.setDate(sourceStartDate.getDate() + 6);

      const sourceResponse = await axios.get(`${API}/schedules`, {
        headers,
        params: {
          date_from: sourceStartDate.toISOString().split('T')[0],
          date_to: sourceEndDate.toISOString().split('T')[0]
        }
      });

      let sourceSchedules = sourceResponse.data || [];

      // Filter schedules based on copy mode
      if (copyWeekData.copyMode === 'employees') {
        sourceSchedules = sourceSchedules.filter(schedule => 
          copyWeekData.selectedEmployees.includes(schedule.user_id)
        );
      } else if (copyWeekData.copyMode === 'days') {
        sourceSchedules = sourceSchedules.filter(schedule => {
          const scheduleDate = new Date(schedule.date.replace(/-/g, '/'));
          const dayName = scheduleDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
          return copyWeekData.selectedDays.includes(dayName);
        });
      }

      if (sourceSchedules.length === 0) {
        toast.error('No schedules found matching your selection criteria');
        return;
      }

      // Calculate target week dates
      const targetStartDate = new Date(copyWeekData.targetWeek);
      const daysDiff = (targetStartDate - sourceStartDate) / (1000 * 60 * 60 * 24);

      // Clear target schedules if overwrite is enabled (filtered by copy mode)
      if (copyWeekData.overwriteExisting) {
        const targetEndDate = new Date(targetStartDate);
        targetEndDate.setDate(targetStartDate.getDate() + 6);

        const targetResponse = await axios.get(`${API}/schedules`, {
          headers,
          params: {
            date_from: targetStartDate.toISOString().split('T')[0],
            date_to: targetEndDate.toISOString().split('T')[0]
          }
        });

        let targetSchedules = targetResponse.data || [];

        // Filter target schedules based on copy mode
        if (copyWeekData.copyMode === 'employees') {
          targetSchedules = targetSchedules.filter(schedule => 
            copyWeekData.selectedEmployees.includes(schedule.user_id)
          );
        } else if (copyWeekData.copyMode === 'days') {
          targetSchedules = targetSchedules.filter(schedule => {
            const scheduleDate = new Date(schedule.date.replace(/-/g, '/'));
            const dayName = scheduleDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
            return copyWeekData.selectedDays.includes(dayName);
          });
        }
        
        // Delete filtered target schedules - do them sequentially to ensure completion
        if (targetSchedules.length > 0) {
          toast.info(`Removing ${targetSchedules.length} existing schedule(s) in target week...`);
          for (const schedule of targetSchedules) {
            try {
              await axios.delete(`${API}/schedules/${schedule.id}`, { headers });
            } catch (deleteError) {
              console.warn(`Failed to delete schedule ${schedule.id}:`, deleteError);
              // Continue with other deletions
            }
          }
          // Small delay to ensure database consistency
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Build the planned new shifts and detect overlaps with existing target-week shifts
      const modeText = copyWeekData.copyMode === 'entire' ? 'week' : 
                       copyWeekData.copyMode === 'employees' ? 'employee schedules' : 'day schedules';

      // Fetch existing target-week shifts for overlap detection (skip when overwriting — those were just deleted)
      let existingTarget = [];
      if (!copyWeekData.overwriteExisting) {
        const tEnd = new Date(targetStartDate);
        tEnd.setDate(targetStartDate.getDate() + 6);
        const tResp = await axios.get(`${API}/schedules`, {
          headers,
          params: { date_from: targetStartDate.toISOString().split('T')[0], date_to: tEnd.toISOString().split('T')[0] }
        });
        existingTarget = (tResp.data || []).filter(s => s.shift_type !== 'pto' && s.shift_type !== 'pending_pto');
      }
      const existingMap = {};
      for (const s of existingTarget) {
        const k = `${s.user_id}|${s.date}`;
        (existingMap[k] = existingMap[k] || []).push(s);
      }

      const plan = sourceSchedules.map((schedule) => {
        const sourceDate = new Date(schedule.date.replace(/-/g, '/'));
        const targetDate = new Date(sourceDate);
        targetDate.setDate(sourceDate.getDate() + daysDiff);
        const dateStr = targetDate.toISOString().split('T')[0];
        const newSchedule = {
          user_id: schedule.user_id,
          shift_template_id: schedule.shift_template_id || '',
          date: dateStr,
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          shift_type: schedule.shift_type,
          role: schedule.role,
          notes: schedule.notes || '',
          work_location: schedule.work_location || 'office'
        };
        const overlaps = (existingMap[`${schedule.user_id}|${dateStr}`] || []).filter(ex =>
          timeRangesOverlap(newSchedule.start_time, newSchedule.end_time, ex.start_time, ex.end_time)
        );
        return { newSchedule, overlaps };
      });

      const conflicts = plan.filter(p => p.overlaps.length > 0);

      if (conflicts.length > 0) {
        // Overlaps detected — ask the manager how to resolve (Replace all / Keep Both / Skip all)
        setCopyOverlap({ open: true, plan, conflicts, headers, modeText, expanded: false });
        return;
      }

      await performCopy(plan, 'keep_both', { headers, modeText });
    } catch (error) {
      console.error('Copy operation failed:', error);
      // Still close modal and refresh even on error - schedules might have partially copied
      toast.error('Copy operation encountered errors. Please check the schedule.');
      await fetchSchedulesForCurrentWeek(true, true);
      setShowCopyWeekModal(false);
      setCopyWeekData({
        sourceWeek: '',
        targetWeek: '',
        overwriteExisting: false,
        copyMode: 'entire',
        selectedEmployees: [],
        selectedDays: []
      });
    }
  };

  const getCurrentWeekStart = (date = currentDate) => {
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    return start.toISOString().split('T')[0];
  };

  // Drag-and-Drop Handler Functions
  const handleDragStart = (e, shift) => {
    if (!isManagerOrAdmin) {
      e.preventDefault();
      return;
    }
    
    setDraggedShift(shift);
    setIsDragging(true);
    
    // Set drag image and data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shift.id);
    
    // Create custom drag image
    const dragElement = e.currentTarget.cloneNode(true);
    dragElement.style.transform = 'rotate(2deg)';
    dragElement.style.opacity = '0.8';
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, 50, 25);
    setTimeout(() => document.body.removeChild(dragElement), 0);
  };

  const handleDragEnd = () => {
    setDraggedShift(null);
    setDragOverCell(null);
    setIsDragging(false);
  };

  const handleDragOver = (e, employee, day) => {
    if (!isManagerOrAdmin || !draggedShift) {
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const cellKey = `${employee.id}-${day.toISOString().split('T')[0]}`;
    setDragOverCell(cellKey);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the cell (not moving to a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCell(null);
    }
  };

  const handleDrop = async (e, targetEmployee, targetDay) => {
    e.preventDefault();
    
    if (!isManagerOrAdmin || !draggedShift) {
      return;
    }

    const targetDate = targetDay.toISOString().split('T')[0];
    const sourceDate = draggedShift.date;

    // Check if dropping on the same cell
    if (draggedShift.user_id === targetEmployee.id && sourceDate === targetDate) {
      setDraggedShift(null);
      setDragOverCell(null);
      setIsDragging(false);
      return;
    }

    // Find real shifts already in the target cell (ignore PTO markers and the shift being dragged)
    const existingShifts = schedules.filter(s =>
      s.user_id === targetEmployee.id &&
      s.date === targetDate &&
      s.id !== draggedShift.id &&
      s.shift_type !== 'pto' &&
      s.shift_type !== 'pending_pto'
    );

    const decision = { draggedShift, targetEmployee, targetDate, targetDay, existingShifts };

    if (existingShifts.length === 0) {
      // Empty target → plain move, no prompt needed
      await applyDrop(decision, 'move');
    } else {
      // Occupied target → ask the manager how to resolve
      setDropDecision(decision);
      setDraggedShift(null);
      setDragOverCell(null);
      setIsDragging(false);
    }
  };

  // Perform the actual shift move based on the chosen mode:
  //  - 'move'    : target is empty, just relocate the dragged shift
  //  - 'add'     : keep existing target shift(s) and add the dragged one alongside
  //  - 'replace' : delete existing target shift(s), then place the dragged one
  //  - 'swap'    : trade places — existing target shift(s) go to the source cell
  const applyDrop = async (decision, mode) => {
    const { draggedShift: ds, targetEmployee, targetDate, targetDay, existingShifts = [] } = decision;
    const sourceEmployeeId = ds.user_id;
    const sourceDate = ds.date;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        window.location.href = '/';
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const q = '?override_duplicate=true&override_holiday=true';

      const makeShift = (src, userId, date, note) => ({
        user_id: userId,
        shift_template_id: src.shift_template_id || '',
        date,
        start_time: src.start_time,
        end_time: src.end_time,
        shift_type: src.shift_type,
        role: src.role,
        work_location: src.work_location || 'office',
        notes: note
      });

      if (mode === 'replace') {
        for (const ex of existingShifts) {
          await axios.delete(`${API}/schedules/${ex.id}`, { headers });
        }
      }

      if (mode === 'swap') {
        // Move each existing target shift back to the source cell
        for (const ex of existingShifts) {
          await axios.post(`${API}/schedules${q}`, makeShift(ex, sourceEmployeeId, sourceDate, `Swapped from ${targetEmployee.name} on ${formatDateSafe(targetDate)}`), { headers });
          await axios.delete(`${API}/schedules/${ex.id}`, { headers });
        }
      }

      // Move the dragged shift into the target cell (all modes)
      await axios.post(`${API}/schedules${q}`, makeShift(ds, targetEmployee.id, targetDate, `Moved from ${ds.employee?.name} on ${formatDateSafe(sourceDate)}`), { headers });
      await axios.delete(`${API}/schedules/${ds.id}`, { headers });

      const verb = mode === 'swap' ? 'Swapped' : mode === 'replace' ? 'Replaced shift for' : 'Moved shift to';
      toast.success(`${verb} ${targetEmployee.name} on ${targetDay.toLocaleDateString()}`);

      await fetchSchedulesForCurrentWeek(true, true);
    } catch (error) {
      console.error('Failed to move shift:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to move shift');
      toast.error(msg);
    } finally {
      setDropDecision(null);
      setDraggedShift(null);
      setDragOverCell(null);
      setIsDragging(false);
    }
  };

  const weekDays = getWeekDays();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full" data-testid="schedule-manager">
      {/* Responsive Styles for Schedule Grid */}
      <style>{`
        /* Ensure schedule fits viewport - RESPONSIVE SIZING */
        #schedule-container {
          width: 100% !important;
          min-height: 500px !important;
          height: calc(100vh - 280px) !important;
          max-height: calc(100vh - 280px) !important;
        }
        
        #daily-timeline-container {
          width: 100% !important;
          min-height: 400px !important;
          height: calc(100vh - 400px) !important;
          max-height: calc(100vh - 400px) !important;
        }
        
        /* Responsive table columns */
        @media (max-width: 1024px) {
          .schedule-table th,
          .schedule-table td {
            min-width: 80px;
            font-size: 0.7rem;
          }
        }
        
        @media (max-width: 768px) {
          .schedule-table th,
          .schedule-table td {
            min-width: 60px;
            font-size: 0.65rem;
            padding: 0.25rem;
          }
        }
        
        @media print {
          /* Hide non-essential elements when printing */
          button, .no-print {
            display: none !important;
          }
          
          /* Optimize layout for print */
          .print-optimize {
            page-break-inside: avoid;
          }
          
          /* Ensure proper sizing */
          @page {
            size: landscape;
            margin: 0.5in;
          }
        }
      `}</style>
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schedule Management</h1>
          <p className="text-gray-600 mt-1">
            Manage team schedules and assignments for the week.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Admin-only operations moved to Admin section */}

          <Dialog open={showPTOModal} onOpenChange={setShowPTOModal}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetPTOForm} 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md"
                data-testid="pto-request-button"
              >
                <Plane className="h-4 w-4 mr-2" />
                Request PTO
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-lg">
              <DialogHeader className="pb-2">
                <DialogTitle className="text-xl font-bold">
                  {selectedEmployeeForPTO ? 'Assign Time Off' : 'Request Time Off'}
                </DialogTitle>
                <DialogDescription className="text-sm">
                  {selectedEmployeeForPTO 
                    ? `Assigning time off for ${selectedEmployeeForPTO.name}`
                    : 'Submit a request for management approval'
                  }
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handlePTOSubmit} className="space-y-4">
                {/* Date Range - Compact */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pto-start-date" className="text-sm font-medium">
                      Start Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pto-start-date"
                      type="date"
                      value={ptoForm.start_date}
                      onChange={(e) => setPtoForm({...ptoForm, start_date: e.target.value})}
                      required
                      data-testid="pto-start-date"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pto-end-date" className="text-sm font-medium">
                      End Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pto-end-date"
                      type="date"
                      value={ptoForm.end_date}
                      onChange={(e) => setPtoForm({...ptoForm, end_date: e.target.value})}
                      required
                      min={ptoForm.start_date}
                      data-testid="pto-end-date"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Type and Reason - Compact */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Type</Label>
                    <Select value={ptoForm.pto_type} onValueChange={(value) => setPtoForm({...ptoForm, pto_type: value})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ptoTypes.filter(type => type.id).map(type => (
                          <SelectItem key={type.id} value={type.id}>{type.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pto-reason" className="text-sm font-medium">Reason (Optional)</Label>
                    <Input
                      id="pto-reason"
                      type="text"
                      placeholder="Add notes..."
                      value={ptoForm.reason}
                      onChange={(e) => setPtoForm({...ptoForm, reason: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Options Row - All checkboxes in one line */}
                <div className="flex flex-wrap gap-4 py-2 px-3 bg-gray-50 rounded-lg">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ptoForm.exclude_weekends}
                      onChange={(e) => setPtoForm({...ptoForm, exclude_weekends: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                    />
                    <span className="text-sm">Exclude weekends</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ptoForm.is_partial_day}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setPtoForm({
                            ...ptoForm,
                            is_partial_day: true,
                            worked_start_time: '',
                            worked_end_time: ''
                          });
                        } else {
                          setPtoForm({ ...ptoForm, is_partial_day: false });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                    />
                    <span className="text-sm">Partial day</span>
                  </label>
                </div>

                {/* Partial Day - worked hours are set against the employee's real shift; PTO fills the rest */}
                {ptoForm.is_partial_day && (
                  <PartialPTOFields
                    key={`${showPTOModal}-${selectedEmployeeForPTO?.id || 'self'}`}
                    ptoForm={ptoForm}
                    setPtoForm={setPtoForm}
                    userId={selectedEmployeeForPTO?.id || (employees.find(emp => emp.email === user?.email) || {}).id || user?.id}
                    lunchPolicy={lunchPolicy}
                    onValidityChange={setPartialPtoValid}
                  />
                )}

                <DialogFooter className="pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowPTOModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={ptoSubmitting || (ptoForm.is_partial_day && !partialPtoValid)} data-testid="pto-submit-button" className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600">
                    <Send className="h-4 w-4 mr-2" />
                    {ptoSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showWFHModal} onOpenChange={(o) => { setShowWFHModal(o); if (!o) setSelectedEmployeeForWFH(null); }}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => {
                  resetWFHForm();
                  openWFHModal();
                }} 
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 shadow-md"
                data-testid="wfh-request-button"
              >
                <Home className="h-4 w-4 mr-2" />
                Request WFH
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{selectedEmployeeForWFH ? `Assign Work From Home — ${selectedEmployeeForWFH.name}` : 'Request Work From Home'}</DialogTitle>
                <DialogDescription>
                  {selectedEmployeeForWFH ? 'Assign a remote day for this employee (applies to the schedule on approval)' : 'Request to work remotely on scheduled days'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleWFHSubmit} className="space-y-4">
                {/* WFH Type Selector */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">WFH Type <span className="text-red-500">*</span></label>
                  <Select
                    value={wfhForm.wfh_type}
                    onValueChange={(value) => setWfhForm({...wfhForm, wfh_type: value})}
                  >
                    <SelectTrigger className={`bg-white ${!wfhForm.wfh_type ? 'border-amber-300' : ''}`} data-testid="wfh-type-select-schedule">
                      <SelectValue placeholder="Select WFH type" />
                    </SelectTrigger>
                    <SelectContent>
                      {wfhTypes
                        .filter(type => type.value && type.value !== 'business_directed')
                        .map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{type.display_name}</span>
                              <span className="text-xs text-gray-500">{type.description}</span>
                            </div>
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                  {!wfhForm.wfh_type && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Please select a WFH type to continue
                    </p>
                  )}
                  {wfhForm.wfh_type && wfhForm.wfh_type !== 'flex_bank' && (
                    <p className="text-xs text-green-600">
                      ✓ This type does not deduct from Flex Bank balance
                    </p>
                  )}
                </div>

                {/* Flex Bank Balance Display - only show for flex_bank type */}
                {wfhForm.wfh_type === 'flex_bank' && flexBankInfo && (
                  <div className={`rounded-lg p-4 border ${
                    flexBankInfo.flex_bank_balance > 5 
                      ? 'bg-green-50 border-green-200' 
                      : flexBankInfo.flex_bank_balance > 0 
                        ? 'bg-yellow-50 border-yellow-200' 
                        : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Your Flex Bank Balance</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {flexBankInfo.flex_bank_balance} <span className="text-sm font-normal">days remaining</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Year {flexBankInfo.flex_bank_year}</p>
                        <p className="text-xs text-gray-500">Used: {flexBankInfo.days_used} of {flexBankInfo.default_days}</p>
                      </div>
                    </div>
                    {flexBankInfo.flex_bank_balance === 0 && flexBankInfo.auto_reject_enabled && (
                      <p className="text-xs text-red-600 mt-2">
                        ⚠️ You have no Flex Bank days remaining. New requests will be automatically rejected.
                      </p>
                    )}
                    {flexBankInfo.flex_bank_balance === 0 && !flexBankInfo.auto_reject_enabled && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ You have no Flex Bank days remaining. Manager approval required for exceptions.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Historical Toggle */}
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border">
                  <input
                    type="checkbox"
                    id="historical-wfh"
                    checked={isHistoricalWFH}
                    onChange={(e) => {
                      setIsHistoricalWFH(e.target.checked);
                      setSelectedWFHShifts([]);
                      setWfhForm({ ...wfhForm, start_date: '', end_date: '' });
                    }}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="historical-wfh" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Historical Request
                  </label>
                  <span className="text-xs text-gray-500">
                    (Record a past flex day that was already used)
                  </span>
                </div>
                
                {/* HISTORICAL MODE - Date picker for past dates */}
                {isHistoricalWFH && (
                  <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Historical Entry:</strong> Select the date(s) when you worked from home
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="hist-start-date">Start Date *</Label>
                        <Input
                          id="hist-start-date"
                          type="date"
                          max={new Date().toISOString().split('T')[0]}
                          value={wfhForm.start_date}
                          onChange={(e) => setWfhForm({...wfhForm, start_date: e.target.value, end_date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="hist-end-date">End Date *</Label>
                        <Input
                          id="hist-end-date"
                          type="date"
                          max={new Date().toISOString().split('T')[0]}
                          min={wfhForm.start_date}
                          value={wfhForm.end_date}
                          onChange={(e) => setWfhForm({...wfhForm, end_date: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* FUTURE MODE - Show upcoming shifts - CLICKABLE */}
                {selectedEmployeeForWFH && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <p className="text-sm text-teal-800">
                      <strong>Assigning WFH for {wfhForm.start_date}.</strong> The employee's shift that day will be split into remote and office time on approval.
                    </p>
                  </div>
                )}

                {!selectedEmployeeForWFH && !isHistoricalWFH && upcomingShifts.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Select Shifts for WFH *</Label>
                      <span className="text-xs text-gray-500">
                        {selectedWFHShifts.length} selected
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {upcomingShifts.map(shift => {
                        const isSelected = selectedWFHShifts.some(s => s.date === shift.date);
                        return (
                          <div
                            key={shift.date}
                            onClick={() => toggleWFHShift(shift)}
                            data-testid={`wfh-shift-option-${shift.date}`}
                            className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium">
                                    {formatDateWithWeekday(shift.date)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {shift.start_time} - {shift.end_time}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    shift.work_location === 'office' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'
                                  }`}>
                                    {shift.work_location || 'office'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-testid="wfh-shift-count">
                      Showing your shifts for the next 2 weeks. Need a later date? Use the calendar below.
                    </p>
                  </div>
                )}
                
                {!selectedEmployeeForWFH && !isHistoricalWFH && upcomingShifts.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>No shifts in the next 2 weeks.</strong> Use "Pick a later date" below to choose a future shift, 
                      contact your manager to add shifts, or check "Historical Request" above to log a past flex day.
                    </p>
                  </div>
                )}

                {!selectedEmployeeForWFH && !isHistoricalWFH && wfhUserId && (() => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const minDate = new Date(today); minDate.setDate(minDate.getDate() + WFH_QUICK_LIST_DAYS + 1);
                  const maxDate = new Date(today.getFullYear() + 1, 11, 31);
                  return (
                    <WFHLaterDatePicker
                      userId={wfhUserId}
                      minDate={minDate}
                      maxDate={maxDate}
                      selectedShifts={selectedWFHShifts}
                      onToggle={toggleWFHShift}
                    />
                  );
                })()}

                {/* Hours Selection */}
                <div className="space-y-2">
                  <Label>Hours per Day *</Label>
                  <div className="flex space-x-4">
                    {flexBankSettings.allowed_hours.map(hours => (
                      <label
                        key={hours}
                        className={`flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                          wfhForm.hours === hours 
                            ? 'bg-blue-50 border-blue-500 text-blue-700' 
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="wfh-hours"
                          value={hours}
                          checked={wfhForm.hours === hours}
                          onChange={() => setWfhForm({ ...wfhForm, hours })}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="font-medium">{hours} hours</span>
                        <span className="text-xs text-gray-500">
                          ({hours === 8 ? 'Full day' : 'Half day'})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Which part of the shift (only for half-day WFH) */}
                {wfhForm.hours === 4 && (
                  <div className="space-y-2">
                    <Label>Which part of the shift? *</Label>
                    <div className="flex space-x-4">
                      {[{ v: 'morning', l: 'Morning', d: 'First half remote' }, { v: 'afternoon', l: 'Afternoon', d: 'Second half remote' }].map(opt => (
                        <label
                          key={opt.v}
                          className={`flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                            (wfhForm.wfh_period || 'morning') === opt.v
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                          data-testid={`wfh-period-${opt.v}`}
                        >
                          <input
                            type="radio"
                            name="wfh-period"
                            value={opt.v}
                            checked={(wfhForm.wfh_period || 'morning') === opt.v}
                            onChange={() => setWfhForm({ ...wfhForm, wfh_period: opt.v })}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="font-medium">{opt.l}</span>
                          <span className="text-xs text-gray-500">({opt.d})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {!isHistoricalWFH && selectedWFHShifts.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Selected:</strong> {selectedWFHShifts.length} shift(s) from {wfhForm.start_date} to {wfhForm.end_date}
                      {wfhForm.wfh_type === 'flex_bank' && (
                        <>
                          <br />
                          <span className="text-xs">
                            This will use approximately {selectedWFHShifts.length * (wfhForm.hours / 8)} Flex Bank day(s)
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Selected dates summary - Historical mode */}
                {isHistoricalWFH && wfhForm.start_date && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Historical Entry:</strong> {wfhForm.start_date === wfhForm.end_date 
                        ? wfhForm.start_date 
                        : `${wfhForm.start_date} to ${wfhForm.end_date}`}
                      {wfhForm.wfh_type === 'flex_bank' && (
                        <>
                          <br />
                          <span className="text-xs">
                            This will deduct from your Flex Bank balance
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="wfh-reason">Reason (Optional)</Label>
                  <Input
                    id="wfh-reason"
                    type="text"
                    placeholder="Add any additional context..."
                    value={wfhForm.reason}
                    onChange={(e) => setWfhForm({...wfhForm, reason: e.target.value})}
                    data-testid="wfh-reason-input"
                  />
                </div>

                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowWFHModal(false)}
                    data-testid="cancel-wfh-button"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    data-testid="submit-wfh-button"
                    className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                    disabled={!wfhForm.wfh_type || (selectedEmployeeForWFH ? !wfhForm.start_date : (isHistoricalWFH ? !wfhForm.start_date : selectedWFHShifts.length === 0))}
                  >
                    <Home className="h-4 w-4 mr-2" />
                    {!wfhForm.wfh_type 
                      ? 'Select WFH Type'
                      : selectedEmployeeForWFH
                        ? (wfhForm.start_date ? `Assign WFH to ${selectedEmployeeForWFH.name}` : 'Select Date')
                        : isHistoricalWFH 
                          ? (wfhForm.start_date ? 'Submit Historical Request' : 'Select Date')
                          : (selectedWFHShifts.length === 0 
                              ? 'Select Shifts' 
                              : `Submit ${selectedWFHShifts.length} Day Request`)}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showQuickSchedule} onOpenChange={setShowQuickSchedule}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetQuickScheduleForm} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="quick-schedule-button"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Quick Schedule
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Quick Schedule - Multiple Days</DialogTitle>
                <DialogDescription>
                  Apply a shift template to multiple days at once for an employee.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleQuickScheduleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quick-employee_id">Employee *</Label>
                    <Select 
                      value={quickScheduleData.user_id} 
                      onValueChange={(value) => setQuickScheduleData({...quickScheduleData, user_id: value})}
                      required
                    >
                      <SelectTrigger data-testid="quick-schedule-employee-select">
                        <SelectValue placeholder="Select an employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(employee => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.name} - {employee.role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quick-shift_template_id">Shift Template *</Label>
                    <Select 
                      value={quickScheduleData.shift_template_id} 
                      onValueChange={(value) => setQuickScheduleData({...quickScheduleData, shift_template_id: value})}
                      required
                    >
                      <SelectTrigger data-testid="quick-schedule-template-select">
                        <SelectValue placeholder="Select a shift template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(template => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name} ({formatTime(template.start_time)} - {formatTime(template.end_time)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Select Days for This Week</Label>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day, index) => {
                      const dayDate = day.toISOString().split('T')[0];
                      const isSelected = quickScheduleData.days.includes(dayDate);
                      const daySchedules = getSchedulesForDate(day);
                      const hasConflict = daySchedules.some(s => s.user_id === quickScheduleData.user_id);
                      
                      return (
                        <div key={dayDate} className="text-center">
                          <Label className="text-xs font-medium">{dayNames[index].slice(0, 3)}</Label>
                          <div className="mt-1">
                            <button
                              type="button"
                              onClick={() => toggleDay(dayDate)}
                              disabled={hasConflict}
                              className={`w-full p-2 text-xs rounded border-2 transition-colors ${
                                hasConflict 
                                  ? 'bg-red-100 border-red-300 text-red-600 cursor-not-allowed' 
                                  : isSelected 
                                    ? 'bg-blue-500 border-blue-500 text-white' 
                                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                              }`}
                              data-testid={`quick-schedule-day-${index}`}
                            >
                              {day.getDate()}
                              {hasConflict && <div className="text-xs mt-1">Conflict</div>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-gray-600">
                    Selected {quickScheduleData.days.length} day(s). Red days have scheduling conflicts.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quick-notes">Notes</Label>
                  <Input
                    id="quick-notes"
                    type="text"
                    placeholder="Optional notes for all scheduled shifts..."
                    value={quickScheduleData.notes}
                    onChange={(e) => setQuickScheduleData({...quickScheduleData, notes: e.target.value})}
                    data-testid="quick-schedule-notes-input"
                  />
                </div>

                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowQuickSchedule(false)}
                    data-testid="cancel-quick-schedule-button"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    data-testid="save-quick-schedule-button"
                    disabled={quickScheduleData.days.length === 0}
                  >
                    Create {quickScheduleData.days.length} Schedule(s)
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-schedule-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Single Schedule
              </Button>
            </DialogTrigger>
          
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0 pb-4 border-b">
              <DialogTitle className="text-2xl font-bold">{editingSchedule ? (isPtoBlockOpen ? (editingSchedule.shift_type === 'pending_pto' ? 'Time Off Request' : 'Time Off') : 'Edit Schedule') : 'Create New Shift'}</DialogTitle>
              <DialogDescription className="text-base">
                {editingSchedule ? (isPtoBlockOpen ? (editingSchedule.shift_type === 'pending_pto' ? 'This request is waiting for manager approval.' : 'Review or remove this time off.') : 'Update the shift details for this employee.') : 'Configure shift details and assign to an employee.'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="space-y-6 flex-1 overflow-y-auto pr-2 py-4">
              
              {isPtoBlockOpen && (() => {
                const typeObj = ptoTypes.find(t => t.id === editingSchedule.role || t.value === editingSchedule.role) || {};
                const ptoTypeName = editingPtoRequest?.pto_type_display || typeObj.display_name || typeObj.name || 'Time Off';
                const isAllDay = (editingSchedule.start_time === '00:00' && editingSchedule.end_time === '23:59');
                const fmt = (t) => {
                  if (!t) return '';
                  const [h, m] = t.split(':').map(Number);
                  const ap = h >= 12 ? 'PM' : 'AM';
                  const hh = h % 12 === 0 ? 12 : h % 12;
                  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
                };
                const fmtStamp = (iso) => {
                  if (!iso) return '';
                  const d = new Date(iso);
                  return isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                };
                const reason = editingPtoRequest
                  ? (editingPtoRequest.reason || '')
                  : (editingSchedule.notes || '').split(' - ').slice(1).join(' - ').trim();
                const status = editingPtoRequest?.status || (isPendingPtoOpen ? 'pending' : 'approved');
                const canEdit = isManagerOrAdmin && !!editingPtoRequest && status !== 'cancelled';
                const requestedBy = editingPtoRequest?.requested_by_name;
                const approvedBy = editingPtoRequest?.approved_by_name;
                return (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-5 border border-amber-200 space-y-4" data-testid="pto-info-panel">
                    <div className="flex items-center space-x-2">
                      <Plane className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Time Off Details</h3>
                      <span className="ml-auto text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 capitalize" data-testid="pto-info-status">{status}</span>
                      {canEdit && !ptoDetailEdit && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-xs"
                          data-testid="pto-edit-details-button"
                          onClick={() => setPtoDetailEdit({
                            pto_type: (ptoTypes.find(t => t.id === editingPtoRequest.pto_type || t.value === editingPtoRequest.pto_type) || {}).id || '',
                            reason: editingPtoRequest.reason || ''
                          })}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <p className="text-gray-500">Employee</p>
                        <p className="font-medium text-gray-900" data-testid="pto-info-employee">{editingSchedule.employee?.name || editingSchedule.user?.name || 'Employee'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Type</p>
                        {ptoDetailEdit ? (
                          <Select value={ptoDetailEdit.pto_type} onValueChange={(v) => setPtoDetailEdit({ ...ptoDetailEdit, pto_type: v })}>
                            <SelectTrigger className="h-8 mt-0.5 bg-white" data-testid="pto-edit-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              {ptoTypes.map(t => (
                                <SelectItem key={t.id} value={t.id} data-testid={`pto-edit-type-option-${t.id}`}>{t.display_name || t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="font-medium text-gray-900 capitalize" data-testid="pto-info-type">{ptoTypeName}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium text-gray-900">{editingSchedule.date}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Coverage</p>
                        <p className="font-medium text-gray-900" data-testid="pto-info-coverage">{isAllDay ? 'All Day' : `${fmt(editingSchedule.start_time)} – ${fmt(editingSchedule.end_time)} (partial)`}</p>
                        {!isAllDay && editingSchedule.lunch_start && editingSchedule.lunch_duration > 0 && (() => {
                          const [lh, lm] = editingSchedule.lunch_start.split(':').map(Number);
                          const endMin = lh * 60 + lm + editingSchedule.lunch_duration;
                          const lunchEnd = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                          return <p className="text-xs text-gray-500" data-testid="pto-info-lunch">Includes unpaid lunch {fmt(editingSchedule.lunch_start)} – {fmt(lunchEnd)}</p>;
                        })()}
                      </div>
                      {editingPtoRequest?.start_date && editingPtoRequest?.end_date && editingPtoRequest.start_date !== editingPtoRequest.end_date && (
                        <div className="col-span-2">
                          <p className="text-gray-500">Full Request Range</p>
                          <p className="font-medium text-gray-900">{editingPtoRequest.start_date} → {editingPtoRequest.end_date}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Requested By</p>
                        <p className="font-medium text-gray-900" data-testid="pto-info-requested-by">{requestedBy || 'Not recorded'}</p>
                        {editingPtoRequest?.created_at && <p className="text-xs text-gray-500">{fmtStamp(editingPtoRequest.created_at)}</p>}
                      </div>
                      <div>
                        <p className="text-gray-500">Approved By</p>
                        <p className="font-medium text-gray-900" data-testid="pto-info-approved-by">{approvedBy || (status === 'approved' ? 'Not recorded' : '—')}</p>
                        {editingPtoRequest?.approved_at && <p className="text-xs text-gray-500">{fmtStamp(editingPtoRequest.approved_at)}</p>}
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500">Reason</p>
                        {ptoDetailEdit ? (
                          <Textarea
                            value={ptoDetailEdit.reason}
                            onChange={(e) => setPtoDetailEdit({ ...ptoDetailEdit, reason: e.target.value })}
                            placeholder="Reason for time off"
                            rows={2}
                            className="mt-0.5 bg-white"
                            data-testid="pto-edit-reason-input"
                          />
                        ) : (
                          <p className="font-medium text-gray-900" data-testid="pto-info-reason">{reason || '—'}</p>
                        )}
                      </div>
                    </div>
                    {ptoDetailEdit && (
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setPtoDetailEdit(null)} data-testid="pto-edit-cancel-button">Cancel</Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={ptoDetailSaving || !ptoDetailEdit.pto_type}
                          onClick={handleSavePTODetails}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="pto-edit-save-button"
                        >
                          {ptoDetailSaving ? 'Saving…' : 'Save Changes'}
                        </Button>
                      </div>
                    )}
                    {(isManagerOrAdmin || (isPendingPtoOpen && editingSchedule.user_id === user?.id)) && !ptoDetailEdit && (
                      <div>
                        <Label htmlFor="pto-remove-reason" className="text-sm text-gray-600">{isPendingPtoOpen ? 'Reason for cancelling (optional)' : 'Reason for removal (optional)'}</Label>
                        <Textarea
                          id="pto-remove-reason"
                          value={ptoRemoveReason}
                          onChange={(e) => setPtoRemoveReason(e.target.value)}
                          placeholder="e.g. Plans changed, employee is working this day"
                          rows={2}
                          className="mt-1 bg-white"
                          data-testid="pto-remove-reason-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Only used if you {isPendingPtoOpen ? 'cancel this request' : 'remove this time off'}. Saved to the audit log.</p>
                      </div>
                    )}
                    <div className="flex items-start gap-2 text-xs text-gray-600 bg-white/70 rounded-md p-3 border border-amber-100">
                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{isPendingPtoOpen
                        ? 'This request has not been approved yet, so the schedule has not changed. Once approved, these PTO hours replace that part of the shift.'
                        : (editingPtoRequest?.start_date && editingPtoRequest.start_date !== editingPtoRequest.end_date
                          ? "Removing this time off restores the employee's original shift for this day only. The rest of the request stays in place."
                          : "Removing this time off will cancel the request and restore the employee's original shift for this day.")}</span>
                    </div>
                  </div>
                );
              })()}

              {!isPtoBlockOpen && (<>
              {/* Employee & Template Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-200">
                <div className="flex items-center space-x-2 mb-4">
                  <Users className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Employee Assignment</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="user_id" className="text-sm font-medium flex items-center">
                      Employee <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <select
                      id="user_id"
                      value={formData.user_id}
                      onChange={(e) => {
                        const value = e.target.value;
                        const selectedEmployee = employees.find(emp => emp.id === value);
                        if (selectedEmployee && selectedEmployee.role) {
                          setFormData({
                            ...formData, 
                            user_id: value,
                            role: selectedEmployee.role
                          });
                        } else {
                          setFormData({...formData, user_id: value});
                        }
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select employee...</option>
                      {employees
                        .filter(emp => {
                          if (roleFilter === 'all') return true;
                          // Show if user's role matches OR if they manage this role
                          return emp.role === roleFilter || (emp.managed_roles && emp.managed_roles.includes(roleFilter));
                        })
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name || emp.username} • {emp.role}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shift_template_id" className="text-sm font-medium">Shift Template (Optional)</Label>
                    <Select 
                      value={formData.shift_template_id} 
                      onValueChange={handleTemplateChange}
                    >
                      <SelectTrigger data-testid="schedule-template-select" className="bg-white">
                        <SelectValue placeholder="Select template or custom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span>Custom Shift (No Template)</span>
                          </div>
                        </SelectItem>
                        {templates.map(template => (
                          <SelectItem key={template.id} value={template.id}>
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4 text-blue-500" />
                              <span>{template.name}</span>
                              <span className="text-xs text-gray-500">({formatTime(template.start_time)} - {formatTime(template.end_time)})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Shift Details Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-5 border border-purple-200">
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Shift Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shift_type" className="text-sm font-medium flex items-center">
                      Shift Type <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Select 
                      value={formData.shift_type} 
                      onValueChange={(value) => {
                        // When on_call is selected, clear work_location (not applicable for on-call)
                        if (value === 'on_call') {
                          setFormData({...formData, shift_type: value, work_location: ''});
                        } else {
                          setFormData({...formData, shift_type: value});
                        }
                      }}
                      required
                    >
                      <SelectTrigger data-testid="schedule-shift-type-select" className="bg-white">
                        <SelectValue placeholder="Select shift type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-blue-600" />
                            <span>Regular Shift</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="on_call">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-orange-600" />
                            <span>On-Call Shift</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-sm font-medium flex items-center">
                      Role/Tier <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {availableRoles.length > 0 ? (
                        availableRoles.map((roleData) => (
                          <option key={roleData.role} value={roleData.role}>
                            {getRoleDisplayName(roleData.role)}
                          </option>
                        ))
                      ) : (
                        <option value="tier1">Tier 1</option>
                      )}
                    </select>
                  </div>

                  {/* Work Location - Only show for regular shifts, not on-call */}
                  {formData.shift_type !== 'on_call' && (
                    <div className="space-y-2">
                      <Label htmlFor="work_location" className="text-sm font-medium flex items-center">
                        Work Location <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Select 
                        value={formData.work_location} 
                        onValueChange={(value) => setFormData({...formData, work_location: value})}
                        required
                      >
                        <SelectTrigger data-testid="schedule-location-select" className="bg-white">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="office">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-blue-600" />
                              <span>Office</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="remote">
                            <div className="flex items-center space-x-2">
                              <Home className="h-4 w-4 text-green-600" />
                              <span>Remote</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="hybrid">
                            <div className="flex items-center space-x-2">
                              <Users className="h-4 w-4 text-purple-600" />
                              <span>Hybrid</span>
                            </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time" className="text-sm font-medium flex items-center">
                      Start Time <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                      required
                      data-testid="schedule-start-time"
                      className="bg-white text-lg font-medium"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time" className="text-sm font-medium flex items-center">
                      End Time <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                      required
                      data-testid="schedule-end-time"
                      className="bg-white text-lg font-medium"
                    />
                  </div>
                </div>
              </div>

              {/* Date Selection Section */}
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-5 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Dates</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="multiple-days"
                      checked={enableMultipleDays}
                      onChange={(e) => {
                        setEnableMultipleDays(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedDaysOfWeek([]);
                          setScheduleStartDate('');
                          setScheduleWeeksCount(1);
      setScheduleDurationMode('weeks');
      setScheduleEndDate('');
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="multiple-days" className="text-sm font-medium text-green-700 cursor-pointer">
                      Schedule Multiple Days
                    </label>
                  </div>
                </div>
                
                {!enableMultipleDays ? (
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-sm font-medium flex items-center">
                      Date <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                      data-testid="schedule-date-input"
                      className="bg-white text-lg"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Days of Week Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Select Days of Week</Label>
                      <div className="grid grid-cols-7 gap-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              if (selectedDaysOfWeek.includes(index)) {
                                setSelectedDaysOfWeek(selectedDaysOfWeek.filter(d => d !== index));
                              } else {
                                setSelectedDaysOfWeek([...selectedDaysOfWeek, index]);
                              }
                            }}
                            className={`p-3 text-sm font-semibold rounded-lg border-2 transition-all ${
                              selectedDaysOfWeek.includes(index)
                                ? 'bg-green-600 text-white border-green-600 shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:shadow-sm'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Start Date */}
                    <div className="space-y-2">
                      <Label htmlFor="start-date" className="text-sm font-medium">Start Date</Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={scheduleStartDate}
                        onChange={(e) => setScheduleStartDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="bg-white"
                      />
                    </div>

                    {/* Duration Mode Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Schedule Duration</Label>
                      <div className="flex items-center space-x-4 bg-white p-3 rounded-lg border">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="duration-mode"
                            value="weeks"
                            checked={scheduleDurationMode === 'weeks'}
                            onChange={(e) => setScheduleDurationMode(e.target.value)}
                            className="w-4 h-4 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium">Number of Weeks</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="duration-mode"
                            value="endDate"
                            checked={scheduleDurationMode === 'endDate'}
                            onChange={(e) => setScheduleDurationMode(e.target.value)}
                            className="w-4 h-4 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium">End Date</span>
                        </label>
                      </div>
                    </div>

                    {/* Number of Weeks (conditional) */}
                    {scheduleDurationMode === 'weeks' && (
                      <div className="space-y-2">
                        <Label htmlFor="weeks-count" className="text-sm font-medium">Number of Weeks</Label>
                        <Input
                          id="weeks-count"
                          type="number"
                          min="1"
                          max="52"
                          value={scheduleWeeksCount}
                          onChange={(e) => setScheduleWeeksCount(parseInt(e.target.value) || 1)}
                          required
                          className="bg-white"
                        />
                      </div>
                    )}

                    {/* End Date (conditional) */}
                    {scheduleDurationMode === 'endDate' && (
                      <div className="space-y-2">
                        <Label htmlFor="schedule-end-date" className="text-sm font-medium">End Date</Label>
                        <Input
                          id="schedule-end-date"
                          type="date"
                          value={scheduleEndDate}
                          onChange={(e) => setScheduleEndDate(e.target.value)}
                          min={scheduleStartDate || new Date().toISOString().split('T')[0]}
                          required
                          className="bg-white"
                        />
                      </div>
                    )}

                    {/* Summary */}
                    {selectedDaysOfWeek.length > 0 && scheduleStartDate && (
                      <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-300 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-blue-900 mb-1">Schedule Summary</p>
                            <p className="text-sm text-blue-800">
                              {scheduleDurationMode === 'weeks' ? (
                                <>
                                  Will create <strong className="text-blue-900">{selectedDaysOfWeek.length * scheduleWeeksCount}</strong> shifts
                                  <span className="text-xs ml-1">({selectedDaysOfWeek.length} days × {scheduleWeeksCount} week{scheduleWeeksCount > 1 ? 's' : ''})</span>
                                </>
                              ) : scheduleEndDate ? (
                                <>
                                  Shifts from <strong className="text-blue-900">{scheduleStartDate}</strong> to <strong className="text-blue-900">{scheduleEndDate}</strong>
                                </>
                              ) : (
                                'Select an end date to see shift count'
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Shift Duration Information */}
              {formData.start_time && formData.end_time && (
                <div className={`p-4 rounded-lg border ${
                  needsLunchBreak(formData.start_time, formData.end_time) 
                    ? 'bg-orange-50 border-orange-200' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className={`h-5 w-5 ${
                      needsLunchBreak(formData.start_time, formData.end_time) 
                        ? 'text-orange-600' 
                        : 'text-blue-600'
                    }`} />
                    <h3 className="font-medium text-gray-900">Shift Duration</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Time:</span>
                      <span className="font-medium ml-2">
                        {getTotalShiftTime(formData.start_time, formData.end_time, formData.shift_type).toFixed(1)} hours
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Working Hours:</span>
                      <span className="font-medium ml-2">
                        {getWorkingHours(formData.start_time, formData.end_time, formData.shift_type).toFixed(1)} hours
                      </span>
                    </div>
                  </div>
                  
                  {needsLunchBreak(formData.start_time, formData.end_time, formData.shift_type) && (
                    <div className="mt-2 p-2 bg-white rounded border border-orange-300">
                      <p className="text-xs text-gray-600">
                        <strong>Lunch Period:</strong> Lunch break is automatically deducted from shift duration per your Lunch & Breaks policy in Admin settings. 
                        Employee works {getWorkingHours(formData.start_time, formData.end_time, formData.shift_type).toFixed(1)} hours 
                        but is scheduled from {formatTime(formData.start_time)} to {formatTime(formData.end_time)}.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Information Section */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg p-5 border border-gray-200">
                <div className="flex items-center space-x-2 mb-4">
                  <Edit className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Add any relevant notes or comments..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    data-testid="schedule-notes-input"
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-500">These notes will be visible to managers and the assigned employee.</p>
                </div>
              </div>
              </>)}
              </div>

              <DialogFooter className="flex-shrink-0 bg-gradient-to-r from-gray-50 to-slate-50 border-t-2 pt-5 mt-6">
                <div className="flex justify-between w-full items-center">
                  <div className="flex items-center gap-3">
                    {editingSchedule && isManagerOrAdmin && !isPtoBlockOpen && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const emp = employees.find(e => e.id === editingSchedule.user_id);
                          setPtoForm({
                            start_date: editingSchedule.date,
                            end_date: editingSchedule.date,
                            pto_type: (ptoTypes[0] && ptoTypes[0].id) || 'pto',
                            reason: '',
                            is_partial_day: false,
                            start_time: editingSchedule.start_time || '09:00',
                            end_time: editingSchedule.end_time || '17:00',
                            worked_start_time: '',
                            worked_end_time: '',
                            exclude_weekends: false
                          });
                          setSelectedEmployeeForPTO(emp || null);
                          setShowModal(false);
                          setShowPTOModal(true);
                        }}
                        data-testid="edit-modal-add-pto-button"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50 px-4"
                      >
                        <Plane className="h-4 w-4 mr-2" />
                        Add PTO
                      </Button>
                    )}
                    {!isPtoBlockOpen && (
                      <div className="text-xs text-gray-500">
                        <span className="text-red-500">*</span> Required fields
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowModal(false)}
                      data-testid="cancel-schedule-button"
                      className="px-6"
                    >
                      {isPtoBlockOpen ? 'Close' : 'Cancel'}
                    </Button>
                    {isPendingPtoOpen ? (
                      <>
                        {isManagerOrAdmin && (
                          <Button type="button" onClick={handleApprovePendingPTO} data-testid="approve-pending-pto-button"
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 shadow-md">
                            <CheckCircle className="h-4 w-4 mr-2" /> Approve
                          </Button>
                        )}
                        {(isManagerOrAdmin || editingSchedule.user_id === user?.id) && (
                          <Button type="button" onClick={handleCancelPendingPTO} data-testid="cancel-pending-pto-button"
                            className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white px-6 shadow-md">
                            <Trash2 className="h-4 w-4 mr-2" /> Cancel Request
                          </Button>
                        )}
                      </>
                    ) : isPtoBlockOpen ? (
                      isManagerOrAdmin && (
                        <Button
                          type="button"
                          onClick={handleRemovePTOFromModal}
                          data-testid="remove-pto-button"
                          className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white px-8 shadow-md"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove PTO
                        </Button>
                      )
                    ) : (
                      <Button 
                        type="submit" 
                        data-testid="submit-schedule-button"
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 shadow-md"
                      >
                        {editingSchedule ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Update Shift
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Shift
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Manager/Admin Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {scheduleToDelete?.shift_type === 'pto' ? 'Remove PTO' : 'Delete Shift'}
              </DialogTitle>
              <DialogDescription>
                {scheduleToDelete?.shift_type === 'pto' ? (
                  <>
                    Are you sure you want to remove this PTO from the schedule?
                    <br />
                    <strong>Employee:</strong> {scheduleToDelete?.employee?.name}
                    <br />
                    <strong>Date:</strong> {scheduleToDelete?.date}
                    <br />
                    <strong>Time:</strong> {scheduleToDelete && formatTime(scheduleToDelete.start_time)} - {scheduleToDelete && formatTime(scheduleToDelete.end_time)}
                    <br />
                    <br />
                    <span className="text-yellow-600 font-medium">Note: This only removes the PTO from the schedule. The original PTO request will remain in PTO Management.</span>
                  </>
                ) : (
                  <>
                    Are you sure you want to delete this shift for{' '}
                    {scheduleToDelete?.employee?.name}?
                    <br />
                    <strong>
                      {scheduleToDelete && formatTime(scheduleToDelete.start_time)} - {scheduleToDelete && formatTime(scheduleToDelete.end_time)}
                    </strong>
                    <br />
                    This action cannot be undone.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setScheduleToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (scheduleToDelete) {
                    await handleScheduleDelete(scheduleToDelete.id, scheduleToDelete.shift_type === 'pto');
                    setShowDeleteConfirm(false);
                    setScheduleToDelete(null);
                  }
                }}
              >
                {scheduleToDelete?.shift_type === 'pto' ? 'Remove PTO' : 'Delete Shift'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Swap Request Modal */}
        <Dialog open={showSwapModal} onOpenChange={setShowSwapModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <ArrowLeftRight className="h-5 w-5 text-blue-600" />
                <span>Request Shift Swap</span>
              </DialogTitle>
              <DialogDescription>
                Create a swap request for this shift. Team members with the same role can claim it.
              </DialogDescription>
            </DialogHeader>

            {scheduleToSwap && (
              <div className="space-y-4">
                {/* Shift Details */}
                <div className="p-3 bg-blue-50 rounded-lg space-y-1">
                  <p className="text-sm font-semibold text-blue-900">Shift Details:</p>
                  <p className="text-sm text-blue-800">
                    <strong>Date:</strong> {scheduleToSwap.date}
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Time:</strong> {scheduleToSwap.start_time} - {scheduleToSwap.end_time}
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Type:</strong> {scheduleToSwap.shift_type === 'on_call' ? 'On-Call' : scheduleToSwap.work_location === 'remote' ? 'WFH' : 'Regular'}
                  </p>
                </div>

                {/* Reason */}
                <div>
                  <Label htmlFor="swap-reason">Reason (Optional)</Label>
                  <Textarea
                    id="swap-reason"
                    value={swapReason}
                    onChange={(e) => setSwapReason(e.target.value)}
                    placeholder="E.g., Doctor appointment, family event..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                {/* Manager Approval Requirement - Always enforced */}
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="swap-requires-approval"
                      checked={true}
                      disabled={true}
                      className="mt-0.5 w-4 h-4 text-blue-600 rounded opacity-60 cursor-not-allowed"
                    />
                    <div>
                      <Label htmlFor="swap-requires-approval" className="text-sm font-medium text-yellow-900">
                        Manager approval required
                      </Label>
                      <p className="text-xs text-yellow-700 mt-1">
                        All shift swaps must be approved by a manager or team lead for scheduling integrity.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Only team members with the same role can accept this swap request.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSwapModal(false);
                  setScheduleToSwap(null);
                  setSwapReason('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleRequestSwap}>
                Create Swap Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Enhanced Copy Week Modal */}
        <Dialog open={showCopyWeekModal} onOpenChange={setShowCopyWeekModal}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Copy Schedules</DialogTitle>
              <DialogDescription>
                Copy schedules from one week to another with flexible options.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 overflow-y-auto flex-1 pr-2">
              {/* Copy Mode Selection */}
              <div className="space-y-3">
                <Label>Copy Mode</Label>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="copyMode"
                      value="entire"
                      checked={copyWeekData.copyMode === 'entire'}
                      onChange={(e) => setCopyWeekData({...copyWeekData, copyMode: e.target.value, selectedEmployees: [], selectedDays: []})}
                      className="text-purple-600"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Copy Entire Week</div>
                      <div className="text-sm text-gray-600">Copy all employees for all days</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="copyMode"
                      value="employees"
                      checked={copyWeekData.copyMode === 'employees'}
                      onChange={(e) => setCopyWeekData({...copyWeekData, copyMode: e.target.value, selectedDays: []})}
                      className="text-purple-600"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Copy Specific Employees</div>
                      <div className="text-sm text-gray-600">Copy selected employees for all days</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="copyMode"
                      value="days"
                      checked={copyWeekData.copyMode === 'days'}
                      onChange={(e) => setCopyWeekData({...copyWeekData, copyMode: e.target.value, selectedEmployees: []})}
                      className="text-purple-600"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Copy Specific Days</div>
                      <div className="text-sm text-gray-600">Copy all employees for selected days</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Employee Selection */}
              {copyWeekData.copyMode === 'employees' && (
                <div className="space-y-2">
                  <Label>Select Employees</Label>
                  <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-2">
                    {employees.map(employee => (
                      <label key={employee.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={copyWeekData.selectedEmployees.includes(employee.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyWeekData({...copyWeekData, selectedEmployees: [...copyWeekData.selectedEmployees, employee.id]});
                            } else {
                              setCopyWeekData({...copyWeekData, selectedEmployees: copyWeekData.selectedEmployees.filter(id => id !== employee.id)});
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">{employee.name}</span>
                      </label>
                    ))}
                  </div>
                  {copyWeekData.selectedEmployees.length > 0 && (
                    <p className="text-xs text-green-600">Selected: {copyWeekData.selectedEmployees.length} employee(s)</p>
                  )}
                </div>
              )}

              {/* Day Selection */}
              {copyWeekData.copyMode === 'days' && (
                <div className="space-y-2">
                  <Label>Select Days</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                      <label key={day} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={copyWeekData.selectedDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyWeekData({...copyWeekData, selectedDays: [...copyWeekData.selectedDays, day]});
                            } else {
                              setCopyWeekData({...copyWeekData, selectedDays: copyWeekData.selectedDays.filter(d => d !== day)});
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700 capitalize">{day}</span>
                      </label>
                    ))}
                  </div>
                  {copyWeekData.selectedDays.length > 0 && (
                    <p className="text-xs text-green-600">Selected: {copyWeekData.selectedDays.length} day(s)</p>
                  )}
                </div>
              )}
              
              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source-week">Source Week</Label>
                  <Input
                    id="source-week"
                    type="date"
                    value={copyWeekData.sourceWeek}
                    onChange={(e) => setCopyWeekData({...copyWeekData, sourceWeek: e.target.value})}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="target-week">Target Week</Label>
                  <Input
                    id="target-week"
                    type="date"
                    value={copyWeekData.targetWeek}
                    onChange={(e) => setCopyWeekData({...copyWeekData, targetWeek: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              {/* Overwrite Option */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="overwrite-existing"
                  checked={copyWeekData.overwriteExisting}
                  onChange={(e) => setCopyWeekData({...copyWeekData, overwriteExisting: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="overwrite-existing" className="text-sm text-gray-700">
                  Overwrite existing schedules in target week
                </label>
              </div>
              
              {copyWeekData.overwriteExisting && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will delete matching existing schedules before copying.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex-shrink-0 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCopyWeekModal(false);
                  setCopyWeekData({
                    sourceWeek: '',
                    targetWeek: '',
                    overwriteExisting: false,
                    copyMode: 'entire',
                    selectedEmployees: [],
                    selectedDays: []
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCopyWeek}
                disabled={
                  !copyWeekData.sourceWeek || 
                  !copyWeekData.targetWeek ||
                  (copyWeekData.copyMode === 'employees' && copyWeekData.selectedEmployees.length === 0) ||
                  (copyWeekData.copyMode === 'days' && copyWeekData.selectedDays.length === 0)
                }
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Schedules
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
      </div>
      </div>
      
      {/* Manager/Admin functionality restored with role-based access */}

      {/* Week Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek(-1)}
                data-testid="previous-week-button"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous Week
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek(1)}
                data-testid="next-week-button"
              >
                Next Week
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Today Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                data-testid="today-button"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Today
              </Button>
              
              {/* Refresh Data Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSchedulesForCurrentWeek(true, true)}
                className="hover:bg-gray-100"
                data-testid="refresh-data-button"
                title="Refresh schedule data without losing your position"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>

              {/* Simple Month/Year Picker */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Jump to:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  value={currentDate.getMonth()}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value);
                    const currentMonth = currentDate.getMonth();
                    const newDate = new Date(currentDate);
                    
                    // Handle year rollover: if going from Dec (11) to Jan (0), increment year
                    if (currentMonth === 11 && newMonth === 0) {
                      newDate.setFullYear(newDate.getFullYear() + 1);
                    }
                    // Handle year rollback: if going from Jan (0) to Dec (11), decrement year
                    else if (currentMonth === 0 && newMonth === 11) {
                      newDate.setFullYear(newDate.getFullYear() - 1);
                    }
                    
                    newDate.setMonth(newMonth);
                    setCurrentDate(newDate);
                    
                    // Check if we need to load new schedule range
                    const dateStr = newDate.toISOString().split('T')[0];
                    if (loadedDateRange.start && loadedDateRange.end) {
                      if (dateStr < loadedDateRange.start || dateStr > loadedDateRange.end) {
                        loadScheduleRange(newDate);
                      }
                    }
                  }}
                >
                  <option value="0">January</option>
                  <option value="1">February</option>
                  <option value="2">March</option>
                  <option value="3">April</option>
                  <option value="4">May</option>
                  <option value="5">June</option>
                  <option value="6">July</option>
                  <option value="7">August</option>
                  <option value="8">September</option>
                  <option value="9">October</option>
                  <option value="10">November</option>
                  <option value="11">December</option>
                </select>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  value={currentDate.getFullYear()}
                  onChange={(e) => {
                    const newDate = new Date(currentDate);
                    newDate.setFullYear(parseInt(e.target.value));
                    setCurrentDate(newDate);
                    
                    // Check if we need to load new schedule range
                    const dateStr = newDate.toISOString().split('T')[0];
                    if (loadedDateRange.start && loadedDateRange.end) {
                      if (dateStr < loadedDateRange.start || dateStr > loadedDateRange.end) {
                        loadScheduleRange(newDate);
                      }
                    }
                  }}
                >
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
              </div>
              
              {/* Copy Week Button - Manager/Admin Only */}
              {isManagerOrAdmin && (
                <>
                  <Button 
                    onClick={() => {
                      setCopyWeekData({
                        sourceWeek: getCurrentWeekStart(),
                        targetWeek: '',
                        overwriteExisting: false,
                        copyMode: 'entire',
                        selectedEmployees: [],
                        selectedDays: []
                      });
                      setShowCopyWeekModal(true);
                    }} 
                    variant="outline" 
                    size="sm"
                    className="flex items-center space-x-2 text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Copy className="h-4 w-4" />
                    Copy Week
                  </Button>
                  
                  <Button 
                    onClick={() => setShowRotationsModal(true)} 
                    variant="outline" 
                    size="sm"
                    className="flex items-center space-x-2 text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <RotateCw className="h-4 w-4" />
                    Manage Rotations
                  </Button>
                  
                  {/* Lock/Unlock Schedule */}
                  {weekLocked ? (
                    <Button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const weekStart = getStartOfWeek(currentDate);
                          await axios.delete(`${API}/schedule-locks/${weekStart}`, { headers: { Authorization: `Bearer ${token}` } });
                          setWeekLocked(false);
                          setLockInfo(null);
                          toast.success('Schedule unlocked');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
                      }}
                      variant="outline" size="sm"
                      className="flex items-center space-x-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                      data-testid="unlock-schedule-btn"
                    >
                      <Unlock className="h-4 w-4" />
                      Unlock
                    </Button>
                  ) : (
                    <Button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const weekStart = getStartOfWeek(currentDate);
                          await axios.post(`${API}/schedule-locks/${weekStart}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          setWeekLocked(true);
                          toast.success('Schedule locked - no edits allowed');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to lock'); }
                      }}
                      variant="outline" size="sm"
                      className="flex items-center space-x-2 text-gray-600 border-gray-200 hover:bg-gray-50"
                      data-testid="lock-schedule-btn"
                    >
                      <Lock className="h-4 w-4" />
                      Lock Week
                    </Button>
                  )}
                  
                  {/* Export CSV */}
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const weekStart = getStartOfWeek(currentDate);
                        const res = await axios.get(`${API}/export/schedule-csv?week_start=${weekStart}`, {
                          headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `schedule_${weekStart}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('CSV exported');
                      } catch (e) { toast.error('Export failed'); }
                    }}
                    variant="outline" size="sm"
                    className="flex items-center space-x-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="export-csv-btn"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </Button>
                </>
              )}
            </div>
            
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Week of {weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
            </div>
            
            <div className="flex-1"></div>
          </div>
        </CardContent>
      </Card>

      {/* Locked Week Banner */}
      {weekLocked && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-800 text-sm" data-testid="locked-week-banner">
          <Lock className="h-4 w-4 shrink-0" />
          <span className="font-medium">This week's schedule is locked.</span>
          {lockInfo?.locked_by_name && <span className="text-orange-600">Locked by {lockInfo.locked_by_name}</span>}
          <span className="text-orange-500">Edits are prevented until unlocked by a manager.</span>
        </div>
      )}

      {/* Max Hours Violations */}
      {hoursData?.violations?.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="hours-violations-banner">
          <div className="flex items-center gap-2 text-red-800 text-sm font-medium mb-1">
            <WarningIcon className="h-4 w-4" />
            {hoursData.violations.length} employee(s) exceed {hoursData.max_hours_per_week}h/week limit
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            {hoursData.violations.map(v => (
              <Badge key={v.user_id} variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">
                {v.name}: {v.total_hours}h (+{v.overtime_hours}h OT)
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* View Mode Toggle and Filters */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
                <button
                  onClick={() => setViewMode('weekly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'weekly'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Weekly Schedule
                </button>
                <button
                  onClick={() => setViewMode('daily')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'daily'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Clock className="h-4 w-4 inline mr-2" />
                  Daily Timeline
                </button>
                <button
                  onClick={() => setViewMode('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'monthly'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid="monthly-view-btn"
                >
                  <CalendarDays className="h-4 w-4 inline mr-2" />
                  Monthly
                </button>
              </div>
            </div>
            
            {/* Role Filter - Show for both views but use different filter for daily */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* Shift Type Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Show:</span>
                <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'regular', label: 'Regular', color: 'bg-blue-100 text-blue-700 border-blue-300' },
                    { id: 'on_call', label: 'On-Call', color: 'bg-orange-100 text-orange-700 border-orange-300' },
                    { id: 'pto', label: 'PTO', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setShiftTypeFilter(type.id)}
                      data-testid={`shift-filter-${type.id}`}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                        shiftTypeFilter === type.id
                          ? type.id === 'all'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : type.color + ' shadow-sm border'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-5 w-px bg-gray-300"></div>

              <span className="text-sm font-medium text-gray-700">Role:</span>
              <select
                value={viewMode === 'weekly' ? roleFilter : dailyViewFilters.roleFilter}
                onChange={(e) => {
                  if (viewMode === 'weekly') {
                    setRoleFilter(e.target.value);
                  } else {
                    setDailyViewFilters({ ...dailyViewFilters, roleFilter: e.target.value });
                  }
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles ({employees.filter(e => e.email !== 'admin@workmanager.com').length})</option>
                {/* Dynamically generate role options based on actual employees */}
                {(() => {
                  const roles = [...new Set(employees.filter(e => e.email !== 'admin@workmanager.com').map(e => e.role))];
                  return roles.sort().map(role => (
                    <option key={role} value={role}>
                      {getRoleDisplayName(role)} ({employees.filter(e => e.role === role).length})
                    </option>
                  ));
                })()}
              </select>

              <div className="h-5 w-px bg-gray-300"></div>

              <span className="text-sm font-medium text-gray-700">Location:</span>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="location-filter"
              >
                <option value="all">All Locations</option>
                <option value="office">In Office</option>
                <option value="remote">Remote</option>
              </select>

              {((viewMode === 'weekly' && (roleFilter !== 'all' || locationFilter !== 'all')) || (viewMode === 'daily' && (dailyViewFilters.roleFilter !== 'all' || locationFilter !== 'all'))) && (
                <>
                  <span className="text-sm text-gray-600">
                    Showing {employees
                      .filter(e => e.email !== 'admin@workmanager.com')
                      .filter(e => {
                        const rf = viewMode === 'weekly' ? roleFilter : dailyViewFilters.roleFilter;
                        if (rf === 'all') return true;
                        return e.role === rf || (e.managed_roles && e.managed_roles.includes(rf));
                      })
                      .filter(e => employeeMatchesLocation(e, viewMode === 'weekly' ? schedules : dailyViewSchedules))
                      .length} employee(s)
                  </span>
                  <button
                    onClick={() => {
                      if (viewMode === 'weekly') {
                        setRoleFilter('all');
                      } else {
                        setDailyViewFilters({ ...dailyViewFilters, roleFilter: 'all' });
                      }
                      setLocationFilter('all');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
            
            {/* Print Button for Daily View */}
            {viewMode === 'daily' && (
              <Button
                onClick={() => window.print()}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Conditional View: Weekly Schedule, Daily Timeline, or Monthly Calendar */}
      {viewMode === 'weekly' ? (
        /* Weekly Schedule Grid - Matching Your Layout */
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div ref={scheduleGridRef} className="overflow-auto" style={{minHeight: '500px', height: 'calc(100vh - 280px)', maxHeight: 'calc(100vh - 280px)'}} id="schedule-container">
              <table className="schedule-table w-full border-collapse relative">
              {/* Header Row */}
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-1 text-left font-semibold text-xs text-gray-700 border-r sticky left-0 top-0 bg-gray-50 z-30" style={{minWidth: '100px', maxWidth: '150px', width: '120px'}}>
                    Employee
                  </th>
                  {weekDays.map((day, index) => {
                    const today = new Date();
                    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    const dayString = day.toISOString().split('T')[0];
                    const isToday = dayString === todayString;
                    return (
                      <th key={index} className={`p-1 text-center font-medium text-xs border-r min-w-20 sticky top-0 z-20 ${
                        isToday ? 'bg-blue-50 text-blue-700' : 
                        isHoliday(day) ? 'bg-red-50 text-red-700' : 
                        'bg-gray-50 text-gray-700'
                      }`}>
                        <div className="text-[10px] leading-tight">{dayNames[index].slice(0, 3)}</div>
                        <div className="text-sm font-bold flex items-center justify-center space-x-1">
                          <span>{day.getDate()}</span>
                          {isHoliday(day) && <span className="text-red-500">🎉</span>}
                        </div>
                        {isHoliday(day) && (
                          <div className="text-[8px] text-red-600 font-semibold mt-0.5">{isHoliday(day).name}</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              
              {/* Employee Rows */}
              <tbody>
                {employees
                  .filter(employee => employee.email !== 'admin@workmanager.com') // Hide only the main admin account from schedule
                  .filter(employee => {
                    if (roleFilter === 'all') return true;
                    // Show if user's role matches OR if they manage this role
                    return employee.role === roleFilter || (employee.managed_roles && employee.managed_roles.includes(roleFilter));
                  })
                  .filter(employee => employeeMatchesLocation(employee, schedules))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((employee) => {
                  const employeeSchedules = schedules.filter(s => s.user_id === employee.id);
                  const totalHours = employeeSchedules.reduce((sum, s) => {
                    // Exclude PTO and on-call shifts from hour count
                    if (s.shift_type !== 'pto' && s.shift_type !== 'pending_pto' && s.shift_type !== 'on_call') {
                      const start = new Date(`2000-01-01T${s.start_time}`);
                      const end = new Date(`2000-01-01T${s.end_time}`);
                      const hours = (end - start) / (1000 * 60 * 60);
                      return sum + (hours > 0 ? hours : hours + 24);
                    }
                    return sum;
                  }, 0);
                  
                  return (
                    <tr key={employee.id} className="border-b hover:bg-gray-50">
                      {/* Employee Column - Ultra Compact - Sticky */}
                      <td className="p-1 border-r bg-gray-25 sticky left-0 z-10 bg-white shadow-sm" style={{minWidth: '100px', maxWidth: '150px', width: '120px'}}>
                        <button
                          onClick={() => {
                            if (isManagerOrAdmin) {
                              setEmployeeToClear(employee);
                              setShowClearScheduleModal(true);
                            }
                          }}
                          className={`flex items-center space-x-1 w-full text-left ${isManagerOrAdmin ? 'cursor-pointer hover:bg-blue-50 rounded p-1' : ''}`}
                          title={isManagerOrAdmin ? `Click to clear ${employee.name}'s schedule` : ''}
                        >
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {employee.avatar || employee.name.charAt(0)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-xs text-gray-900 truncate leading-tight">{employee.name}</div>
                            <div className="text-[10px] text-gray-600 truncate">{getRoleDisplayName(employee.role)}</div>
                            <div className="text-[10px] text-gray-500">{totalHours.toFixed(1)}h</div>
                          </div>
                        </button>
                      </td>
                      
                      {/* Day Columns */}
                      {weekDays.map((day, dayIndex) => {
                        const dayString = day.toISOString().split('T')[0];
                        const daySchedules = employeeSchedules.filter(s => s.date === dayString)
                          .filter(s => {
                            if (shiftTypeFilter === 'all') return true;
                            if (shiftTypeFilter === 'pto') return s.shift_type === 'pto' || s.shift_type === 'pending_pto';
                            return s.shift_type === shiftTypeFilter;
                          });
                        // Cross-day carry-ins: overnight shifts from the PREVIOUS day
                        // that continue into this morning (end_time earlier than start_time).
                        const prevDay = new Date(day);
                        prevDay.setDate(prevDay.getDate() - 1);
                        const prevDayString = prevDay.toISOString().split('T')[0];
                        const carryIns = employeeSchedules
                          .filter(s => s.date === prevDayString)
                          .filter(s => {
                            const [sh] = (s.start_time || '0:0').split(':').map(Number);
                            const [eh] = (s.end_time || '0:0').split(':').map(Number);
                            return eh < sh;
                          })
                          .filter(s => {
                            if (shiftTypeFilter === 'all') return true;
                            if (shiftTypeFilter === 'pto') return s.shift_type === 'pto' || s.shift_type === 'pending_pto';
                            return s.shift_type === shiftTypeFilter;
                          });
                        const today = new Date();
                        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                        const isToday = dayString === todayString;
                        const holidayInfo = isHoliday(day);
                        
                        const cellKey = `${employee.id}-${day.toISOString().split('T')[0]}`;
                        const isDropTarget = dragOverCell === cellKey;

                        return (
                          <td 
                            key={dayIndex} 
                            className={`group/cell p-0.5 border-r align-top w-40 ${
                              isToday ? 'bg-blue-50' : holidayInfo ? 'bg-red-50' : ''
                            } ${
                              isManagerOrAdmin && isDropTarget ? 'bg-green-100 border-2 border-green-400 border-dashed' : ''
                            } ${
                              isManagerOrAdmin && isDragging ? 'transition-all duration-200' : ''
                            }`}
                            data-testid={`day-cell-${employee.id}-${dayIndex}`}
                            style={{ maxWidth: '160px' }}
                            onDragOver={(e) => handleDragOver(e, employee, day)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, employee, day)}
                          >
                            <div className="space-y-0.5 min-h-[32px]">
                              {/* Carry-in chips: overnight coverage continuing from the previous night */}
                              {carryIns.map((ci) => {
                                const ciLabel = ci.shift_type === 'on_call' ? 'On-Call' : ci.shift_type === 'regular' ? 'Shift' : (ci.shift_type || 'Shift');
                                return (
                                  <div
                                    key={`carry-${ci.id}`}
                                    className="px-1 py-0.5 rounded text-[11px] italic bg-orange-50 text-orange-700 border border-dashed border-orange-300 flex items-center gap-0.5 leading-tight"
                                    title={`Continued from the previous night — ${ciLabel} ${formatTime(ci.start_time)}–${formatTime(ci.end_time)} runs overnight into this morning`}
                                    data-testid={`carryin-${ci.id}`}
                                  >
                                    <span>↳ {ciLabel} until {formatTime(ci.end_time)}</span>
                                  </div>
                                );
                              })}
                              {daySchedules.map((schedule) => {
                                const isPTO = schedule.shift_type === 'pto';
                                const isPendingPTO = schedule.shift_type === 'pending_pto';
                                const isOnCall = schedule.shift_type === 'on_call';
                                const [schStartHour] = (schedule.start_time || '0:0').split(':').map(Number);
                                const [schEndHour] = (schedule.end_time || '0:0').split(':').map(Number);
                                const isOvernight = schEndHour < schStartHour;
                                
                                const getShiftColor = () => {
                                  if (isPTO) return 'bg-yellow-200 text-yellow-900';
                                  if (isPendingPTO) return 'bg-yellow-100 text-yellow-700 border-2 border-dashed border-yellow-400';
                                  
                                  // Enhanced color scheme with stronger distinction between regular and on-call
                                  const role = schedule.role || 'tier1';
                                  
                                  if (isOnCall) {
                                    // On-call shifts: All orange regardless of role
                                    return 'bg-gradient-to-r from-orange-300 to-orange-400 text-orange-900';
                                  } else {
                                    // Regular shifts: Blue/cool gradient backgrounds
                                    const regularColors = {
                                      'tier1': 'bg-gradient-to-r from-green-200 to-green-300 text-green-900',
                                      'tier2': 'bg-gradient-to-r from-blue-200 to-blue-300 text-blue-900',
                                      'teamlead': 'bg-gradient-to-r from-purple-200 to-purple-300 text-purple-900',
                                      'manager': 'bg-gradient-to-r from-indigo-200 to-indigo-300 text-indigo-900'
                                    };
                                    return regularColors[role] || 'bg-blue-200 text-blue-800';
                                  }
                                };
                                
                                return (
                                  <div
                                    key={schedule.id}
                                    className={`group px-1.5 py-1 rounded text-xs font-medium transition-all duration-200 min-h-[40px] ${getShiftColor()} ${
                                      isOnCall ? 'border border-orange-400' : isPendingPTO ? '' : 'border border-gray-300'
                                    } ${
                                      isManagerOrAdmin && !isPendingPTO ? 'cursor-move hover:shadow-sm' : 'cursor-pointer hover:shadow-sm'
                                    } ${
                                      isDragging && draggedShift?.id === schedule.id ? 'opacity-50' : ''
                                    }`}
                                    data-testid={`shift-${schedule.id}`}
                                    draggable={isManagerOrAdmin && !isPTO}
                                    onDragStart={(e) => handleDragStart(e, schedule)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => {
                                      // Open edit modal when clicking on shift
                                      setEditingSchedule(schedule);
                                      setFormData({
                                        user_id: schedule.user_id,
                                        shift_template_id: schedule.shift_template_id || '',
                                        date: schedule.date,
                                        start_time: schedule.start_time,
                                        end_time: schedule.end_time,
                                        shift_type: schedule.shift_type,
                                        role: schedule.role || 'tier1',
                                        work_location: schedule.work_location || 'office',
                                        notes: schedule.notes || ''
                                      });
                                      setShowModal(true);
                                    }}
                                  >
                                    <div className="flex flex-col space-y-0.5">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          {!isPTO && !isPendingPTO && (
                                            <div className="font-bold text-[13px] truncate leading-tight">
                                              {formatTime(schedule.start_time)}-{formatTime(schedule.end_time)}
                                            </div>
                                          )}
                                          {(isPTO || isPendingPTO) && (
                                            <div className="text-xs font-semibold">
                                              {(schedule.start_time === '00:00' && schedule.end_time === '23:59') 
                                                ? 'All Day' 
                                                : `${formatTime(schedule.start_time)}-${formatTime(schedule.end_time)}`
                                              }
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Shift Type Label */}
                                        <div className="flex items-center space-x-1">
                                          {schedule.shift_type === 'regular' && schedule.wfh_override ? (
                                            <div className={`text-[10px] font-bold uppercase px-1 rounded flex items-center space-x-0.5 ${
                                              schedule.wfh_type === 'on_call' ? 'bg-orange-600 text-white' :
                                              schedule.wfh_type === 'business_directed' ? 'bg-purple-600 text-white' :
                                              'bg-blue-600 text-white'
                                            }`}>
                                              <Home className="h-3 w-3" />
                                              <span>{schedule.wfh_type === 'on_call' ? 'WFH - On Call' : 
                                                     schedule.wfh_type === 'business_directed' ? 'WFH - Business' : 
                                                     'WFH Override'}</span>
                                            </div>
                                          ) : (
                                            <div className={`text-[10px] font-bold uppercase px-1 rounded ${
                                              isPTO ? 'bg-yellow-600 text-white' :
                                              isPendingPTO ? 'bg-yellow-500 text-white' :
                                              isOnCall ? 'bg-orange-600 text-white' :
                                              'bg-green-600 text-white'
                                            }`}>
                                              {isPTO ? 'PTO' : isPendingPTO ? 'Pending' : isOnCall ? 'On-Call' : 'Regular'}
                                            </div>
                                          )}
                                          {schedule.notes && schedule.notes.includes('Swapped from') && (
                                            <ArrowLeftRight className="h-2.5 w-2.5 text-gray-600" title={schedule.notes} />
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Overnight cross-day indicator */}
                                      {isOvernight && !isPTO && !isPendingPTO && (
                                        <div
                                          className="text-[10px] italic text-orange-800 leading-tight flex items-center gap-0.5"
                                          title={`Overnight shift — crosses midnight and continues to ${formatTime(schedule.end_time)} the next morning`}
                                          data-testid={`overnight-indicator-${schedule.id}`}
                                        >
                                          <span>⤷ continues to next morning</span>
                                        </div>
                                      )}
                                      
                                      {/* Location Badge */}
                                      {!isPTO && !isPendingPTO && schedule.work_location && (
                                        <div className="flex items-center space-x-1">
                                          {schedule.work_location === 'remote' && schedule.shift_type !== 'on_call' && schedule.shift_type !== 'pto' && (
                                            <div className={`flex items-center space-x-1 text-[10px] px-1.5 py-0.5 rounded ${
                                              schedule.location_status === 'unplanned' 
                                                ? 'bg-orange-100 text-orange-700 border border-orange-300' 
                                                : 'bg-green-100 text-green-700 border border-green-300'
                                            }`}>
                                              <Home className="h-3 w-3" />
                                              <span className="font-medium">Remote</span>
                                              {schedule.location_status === 'unplanned' && <span>⚠️</span>}
                                            </div>
                                          )}
                                          {schedule.work_location === 'office' && schedule.shift_type !== 'on_call' && schedule.shift_type !== 'pto' && (
                                            <div className="flex items-center space-x-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-300">
                                              <Building className="h-3 w-3" />
                                              <span className="font-medium">Office</span>
                                            </div>
                                          )}
                                          {schedule.work_location === 'hybrid' && schedule.shift_type !== 'on_call' && schedule.shift_type !== 'pto' && (
                                            <div className="flex items-center space-x-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-300">
                                              <Users className="h-3 w-3" />
                                              <span className="font-medium">Hybrid</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Action Buttons */}
                                      <div className="flex items-center space-x-1">
                                        {!isPTO && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingSchedule(schedule);
                                              setFormData({
                                                user_id: schedule.user_id,
                                                shift_template_id: schedule.shift_template_id || '',
                                                date: schedule.date,
                                                start_time: schedule.start_time,
                                                end_time: schedule.end_time,
                                                shift_type: schedule.shift_type,
                                                role: schedule.role || 'tier1',
                                                work_location: schedule.work_location || 'office',
                                                notes: schedule.notes || ''
                                              });
                                              setShowModal(true);
                                            }}
                                            className="p-0.5 bg-blue-100 hover:bg-blue-500 text-blue-600 hover:text-white rounded transition-all"
                                            title="Edit Shift"
                                            data-testid={`edit-shift-${schedule.id}`}
                                          >
                                            <Edit className="h-2.5 w-2.5" />
                                          </button>
                                        )}
                                        
                                        
                                        {/* Request Swap Button - Only show on user's own non-PTO shifts */}
                                        {!isPTO && !isPendingPTO && user && schedule.user_id === user.id && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setScheduleToSwap(schedule);
                                              setShowSwapModal(true);
                                            }}
                                            className="p-0.5 bg-green-100 hover:bg-green-500 text-green-600 hover:text-white rounded transition-all"
                                            title="Request Shift Swap"
                                            data-testid={`swap-shift-${schedule.id}`}
                                          >
                                            <ArrowLeftRight className="h-2.5 w-2.5" />
                                          </button>
                                        )}
                                        
                                        {/* Manager/Admin Delete Button - Now includes PTO */}
                                        {isManagerOrAdmin && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setScheduleToDelete(schedule);
                                              setShowDeleteConfirm(true);
                                            }}
                                            className="p-0.5 bg-red-100 hover:bg-red-500 text-red-600 hover:text-white rounded transition-all"
                                            title={isPTO ? "Remove PTO (Manager Only)" : "Delete Shift (Manager Only)"}
                                            data-testid={`delete-shift-${schedule.id}`}
                                          >
                                            <X className="h-2.5 w-2.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {/* Activity blocks for this day */}
                              {activities
                                .filter(activity => activity.user_id === employee.id && activity.date === dayString)
                                .map((activity) => {
                                  const activityType = activityTypes.find(t => t.value === activity.activity_type) || {};
                                  return (
                                    <div
                                      key={activity.id}
                                      className="px-1 py-1 rounded text-[10px] font-medium cursor-pointer hover:shadow-md transition-all duration-200 overflow-hidden break-words"
                                      style={{ 
                                        backgroundColor: activityType.color + '20', // 20% opacity
                                        border: `1px solid ${activityType.color}`,
                                        color: activityType.color
                                      }}
                                      onClick={() => {
                                        setEditingActivity(activity);
                                        setActivityForm({
                                          ...activity,
                                          user_ids: activity.user_ids || [],
                                          description: activity.description || '',
                                          location: activity.location || '',
                                          client_name: activity.client_name || '',
                                          all_day: activity.all_day || false,
                                          end_date: activity.end_date || '',
                                        });
                                        setShowActivityModal(true);
                                      }}
                                      title={`${activity.title} - ${activity.start_time} to ${activity.end_time}`}
                                    >
                                      <div className="flex items-center space-x-0.5">
                                        <span>{activityType.icon || '📅'}</span>
                                        <span className="font-semibold truncate">{activity.start_time}</span>
                                      </div>
                                      <div className="font-medium mt-0.5 break-words whitespace-normal">{activity.title}</div>
                                      {activity.client_name && (
                                        <div className="text-[8px] break-words whitespace-normal opacity-80">{activity.client_name}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              
                              {/* Compact add-actions row — faint until cell hover */}
                              <div className="flex items-center justify-center gap-1 pt-0.5 opacity-30 group-hover/cell:opacity-100 focus-within:opacity-100 transition-opacity">
                              {(employee.id === user?.id || isManagerOrAdmin) && (
                                <button
                                  onClick={() => {
                                    setActivityForm({
                                      user_id: employee.id,
                                      user_ids: [],
                                      date: day.toISOString().split('T')[0],
                                      end_date: '',
                                      start_time: '09:00',
                                      end_time: '10:00',
                                      all_day: false,
                                      activity_type: activityTypes[0]?.value || '',
                                      title: '',
                                      description: '',
                                      location: '',
                                      client_name: ''
                                    });
                                    setEditingActivity(null);
                                    setShowActivityModal(true);
                                  }}
                                  className="h-6 w-6 flex items-center justify-center rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-400 transition-colors"
                                  title="Add activity (client visit, call, etc.)"
                                  data-testid={`add-activity-${employee.id}-${dayIndex}`}
                                >
                                  <Calendar className="h-3.5 w-3.5" />
                                </button>
                              )}
                              
                              {/* Add shift dropdown menu - Managers and Admins only */}
                              {isManagerOrAdmin && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      className={`h-6 px-2 border border-dashed rounded flex items-center justify-center transition-colors ${weekLocked ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                      data-testid={`add-shift-${employee.id}-${dayIndex}`}
                                      disabled={weekLocked}
                                      title="Add shift"
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                    </button>
                                  </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-48">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setFormData({
                                        user_id: employee.id,
                                        shift_template_id: '',
                                        date: day.toISOString().split('T')[0],
                                        start_time: '08:00',
                                        end_time: '17:00', // 8 hours work + 1 hour lunch = 9 hours total
                                        shift_type: 'regular',
                                        role: employee.role || 'tier1', // Use employee's role
                                        notes: ''
                                      });
                                      setShowModal(true);
                                    }}
                                    data-testid={`add-regular-shift-${employee.id}-${dayIndex}`}
                                  >
                                    <Clock className="h-4 w-4 mr-2 text-blue-600" />
                                    <div>
                                      <div className="font-medium">Regular Shift</div>
                                      <div className="text-xs text-gray-500">Standard work shift</div>
                                    </div>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setFormData({
                                        user_id: employee.id,
                                        shift_template_id: '',
                                        date: day.toISOString().split('T')[0],
                                        start_time: '18:00',
                                        end_time: '08:00',
                                        shift_type: 'on_call',
                                        role: employee.role || 'tier1', // Use employee's role
                                        notes: ''
                                      });
                                      setShowModal(true);
                                    }}
                                    data-testid={`add-oncall-shift-${employee.id}-${dayIndex}`}
                                  >
                                    <AlertCircle className="h-4 w-4 mr-2 text-orange-600" />
                                    <div>
                                      <div className="font-medium">On-Call Shift</div>
                                      <div className="text-xs text-gray-500">Emergency response shift</div>
                                    </div>
                                  </DropdownMenuItem>
                                  
                                  {/* PTO Option - Only for Managers, Team Leads, and Admins */}
                                  {(user?.role === 'manager' || user?.role === 'team_lead' || user?.role === 'admin') && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setPtoForm({
                                          start_date: day.toISOString().split('T')[0],
                                          end_date: day.toISOString().split('T')[0],
                                          pto_type: 'pto',
                                          reason: ''
                                        });
                                        setSelectedEmployeeForPTO(employee);
                                        setShowPTOModal(true);
                                      }}
                                      data-testid={`add-pto-${employee.id}-${dayIndex}`}
                                    >
                                      <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                                      <div>
                                        <div className="font-medium">Add PTO</div>
                                        <div className="text-xs text-gray-500">Paid time off</div>
                                      </div>
                                    </DropdownMenuItem>
                                  )}

                                  {/* WFH Assignment - Only for Managers, Team Leads, and Admins */}
                                  {(user?.role === 'manager' || user?.role === 'team_lead' || user?.role === 'admin') && (
                                    <DropdownMenuItem
                                      onClick={() => openWFHModalForEmployee(employee, day)}
                                      data-testid={`add-wfh-${employee.id}-${dayIndex}`}
                                    >
                                      <Home className="h-4 w-4 mr-2 text-teal-600" />
                                      <div>
                                        <div className="font-medium">Add WFH</div>
                                        <div className="text-xs text-gray-500">Work from home (full or half day)</div>
                                      </div>
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              )}
                              </div>
                              
                              {/* Deletion functionality moved to Admin section */}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      ) : viewMode === 'daily' ? (
        /* Daily Timeline View */
        <Card className="overflow-hidden">
          <CardContent className="p-4">
            <div className="mb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Daily Schedule Timeline
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {timelineAnchor
                      ? '36-hour view: selected day (midnight) → through the next morning'
                      : '36-hour view: 12 hours back (scroll left ←) → Current Hour → 24 hours ahead'}
                  </p>
                </div>

                {/* Day navigation controls */}
                <div className="flex items-center gap-2" data-testid="daily-timeline-day-nav">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shiftTimelineDay(-1)}
                    data-testid="daily-prev-day-btn"
                    title="Previous day"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="date"
                    className="h-9 rounded-md border border-gray-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={toLocalYMD(timelineAnchor || new Date())}
                    onChange={(e) => setTimelineAnchor(e.target.value ? parseLocalDate(e.target.value) : null)}
                    data-testid="daily-date-picker"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shiftTimelineDay(1)}
                    data-testid="daily-next-day-btn"
                    title="Next day"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={timelineAnchor ? 'outline' : 'default'}
                    size="sm"
                    onClick={goToTimelineToday}
                    data-testid="daily-today-btn"
                    title="Jump to the live current-hour view"
                  >
                    Today
                  </Button>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-2" data-testid="daily-timeline-showing-date">
                Showing schedule for {(timelineAnchor || new Date()).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {toLocalYMD(timelineAnchor || new Date()) === toLocalYMD(new Date()) ? ' (today)' : ''}
              </p>
              
              {/* Cross-Day Shift Legend */}
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-yellow-800">
                    <span className="font-semibold">Cross-Day Shifts:</span> Shifts with 
                    <span className="inline-flex items-center mx-1 px-1 py-0.5 bg-yellow-400 text-yellow-900 rounded font-bold">↪</span>
                    started yesterday and continue into today. Dashed border indicates overnight coverage.
                  </div>
                </div>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading schedule...</p>
              </div>
            ) : (
              <div 
                className="overflow-auto" 
                style={{minHeight: '400px', height: 'calc(100vh - 400px)', maxHeight: 'calc(100vh - 400px)', maxWidth: '100%'}}
                id="daily-timeline-container"
                ref={(el) => {
                  if (el && viewMode === 'daily' && !loading && allSchedules.length > 0) {
                    // Set scroll position directly in ref callback
                    const table = el.querySelector('table');
                    if (table) {
                      const firstHourCell = table.querySelector('th:nth-child(2)');
                      if (firstHourCell && el.scrollLeft === 0) {
                        const columnWidth = firstHourCell.offsetWidth;
                        // Scroll to 11 hours instead of 12 so current hour is fully visible
                        const scrollPosition = 11 * columnWidth;
                        el.scrollLeft = scrollPosition;
                        console.log(`🎯 Ref callback: scrolled to ${scrollPosition}px (11 hours, current hour fully visible)`);
                      }
                    }
                  }
                }}
              >
                  {/* Timeline Table */}
                  <table className="border-collapse border border-gray-300 relative">
                    <thead>
                      <tr>
                        {/* Header Row - Hours */}
                        <th className="bg-gray-100 p-2 font-semibold text-xs border-r border-b border-gray-300 sticky left-0 top-0 z-30 min-w-[200px]">
                          Employee
                        </th>
                        {dailyHourlyTimeline.map((hour, index) => {
                          const nowRef = new Date();
                          const isNow = nowRef.getHours() === hour.getHours() && 
                                        toLocalYMD(nowRef) === toLocalYMD(hour);
                          const nowYMD = toLocalYMD(nowRef);
                          const hourYMD = toLocalYMD(hour);
                          // In live mode use relative labels; in selected-day mode
                          // show the actual short date so columns aren't ambiguous.
                          const dayLabel = timelineAnchor
                            ? hour.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                            : (hourYMD === nowYMD ? 'Today' : (hourYMD < nowYMD ? 'Yesterday' : 'Tomorrow'));
                          return (
                            <th
                              key={index}
                              className={`p-2 text-center text-xs font-medium border-b border-gray-300 sticky top-0 z-20 min-w-[60px] ${
                                isNow ? 'bg-blue-200 text-blue-800 border-l-2 border-r-2 border-blue-500' : 'bg-gray-100 text-gray-700 border-r border-gray-300'
                              }`}
                            >
                              <div className="font-semibold">
                                {hour.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })}
                              </div>
                              <div className={`text-[10px] ${isNow ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>
                                {isNow ? 'Now' : dayLabel}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    
                    <tbody>
                      {/* Employee Rows */}
                      {employees
                        .filter(employee => employee.email !== 'admin@workmanager.com')
                        .filter(employee => {
                          if (dailyViewFilters.roleFilter === 'all') return true;
                          // Show if user's role matches OR if they manage this role
                          return employee.role === dailyViewFilters.roleFilter || (employee.managed_roles && employee.managed_roles.includes(dailyViewFilters.roleFilter));
                        })
                        .filter(employee => employeeMatchesLocation(employee, dailyViewSchedules))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((employee) => {
                          const dailySchedules = dailyByEmployee[employee.id] || [];
                          
                          return (
                            <tr key={employee.id}>
                              {/* Employee Name Cell - Sticky */}
                              <td className="bg-white p-2 border-r border-b border-gray-300 sticky left-0 z-10 shadow-sm">
                                <div className="font-medium text-sm text-gray-900 truncate">
                                  {employee.name}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {getRoleDisplayName(employee.role)}
                                </div>
                              </td>
                              
                              {/* Hour Cells */}
                              {dailyHourlyTimeline.map((hour, hourIndex) => {
                                const shiftsInHour = dailySchedules.filter(schedule => 
                                  isShiftInHourSlot(schedule, hour)
                                );
                                
                                const nowRef = new Date();
                                const isNow = nowRef.getHours() === hour.getHours() && 
                                              toLocalYMD(nowRef) === toLocalYMD(hour);
                                
                                return (
                                  <td
                                    key={hourIndex}
                                    className={`border-b border-gray-300 min-h-[60px] p-1 align-top ${
                                      isNow ? 'bg-blue-100 border-l-2 border-r-2 border-blue-500' : 'bg-white border-r border-gray-300'
                                    }`}
                                  >
                                    {/* Render shifts */}
                                    {shiftsInHour.length > 0 && (
                                      <div className="space-y-1">
                                        {shiftsInHour.map((shift, shiftIndex) => {
                                          // Check if this is a WFH override (regular shift changed to remote via WFH request)
                                          const isWFHOverride = shift.wfh_override === true;
                                          const wfhTypeLabel = shift.wfh_type === 'on_call' ? 'WFH - On Call' :
                                                              shift.wfh_type === 'business_directed' ? 'WFH - Business' :
                                                              'WFH Override';
                                          
                                          // Check if this is a cross-day shift from previous day
                                          const [startHour] = shift.start_time.split(':').map(Number);
                                          const [endHour] = shift.end_time.split(':').map(Number);
                                          const isCrossDay = endHour < startHour;
                                          const currentDayString = toLocalYMD(new Date());
                                          const isFromPreviousDay = isCrossDay && shift.date !== currentDayString;
                                          
                                          // Calculate actual coverage time for this day
                                          let displayTime = `${shift.start_time}-${shift.end_time}`;
                                          if (isFromPreviousDay) {
                                            displayTime = `00:00-${shift.end_time}`;
                                          }
                                          
                                          const tooltipText = isFromPreviousDay 
                                            ? `Cross-day shift from ${shift.date}\nOriginal: ${shift.start_time}-${shift.end_time}\nToday's coverage: 00:00-${shift.end_time}`
                                            : `${isWFHOverride ? wfhTypeLabel + ' - Originally office shift' : shift.shift_type === 'on_call' ? 'On-Call' : shift.shift_type.toUpperCase()}\n${shift.start_time}-${shift.end_time}`;
                                          
                                          return (
                                            <div
                                              key={shift.id || shiftIndex}
                                              className={`text-xs rounded px-1 py-0.5 text-white relative ${
                                                isWFHOverride ? (shift.wfh_type === 'on_call' ? 'bg-orange-600' : shift.wfh_type === 'business_directed' ? 'bg-purple-600' : 'bg-blue-600') : getShiftTypeColor(shift.shift_type)
                                              } ${
                                                isFromPreviousDay ? 'border-2 border-dashed border-yellow-300' : ''
                                              }`}
                                              title={tooltipText}
                                            >
                                              {isFromPreviousDay && (
                                                <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 rounded-full w-4 h-4 flex items-center justify-center text-[8px] font-bold" title="Continued from previous day">
                                                  ↪
                                                </div>
                                              )}
                                              <div className="font-semibold truncate flex items-center justify-between">
                                                <span className="flex items-center space-x-1">
                                                  {isFromPreviousDay && <span className="text-yellow-200">↪</span>}
                                                  <span>
                                                    {isWFHOverride ? `🏠 ${wfhTypeLabel}` :
                                                     shift.shift_type === 'on_call' ? 'On-Call' : 
                                                     shift.shift_type === 'pto' ? 'PTO' :
                                                     shift.shift_type === 'regular' ? 'Regular' : 
                                                     shift.shift_type.charAt(0).toUpperCase() + shift.shift_type.slice(1)}
                                                  </span>
                                                </span>
                                                {shift.notes && shift.notes.includes('Swapped from') && (
                                                  <ArrowLeftRight className="h-3 w-3" title={shift.notes} />
                                                )}
                                              </div>
                                              {isFromPreviousDay && (
                                                <div className="text-[9px] text-yellow-100 mt-0.5">
                                                  From {formatDateSafe(shift.date, {month: 'short', day: 'numeric'})}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    
                                    {/* Render activities for this hour - only show at start time */}
                                    {activities
                                      .filter(activity => {
                                        // Filter activities for this employee and date
                                        if (activity.user_id !== employee.id) return false;
                                        if (activity.date !== currentDate.toISOString().split('T')[0]) return false;
                                        
                                        // Only show in the hour when activity STARTS
                                        const [activityHour] = activity.start_time.split(':').map(Number);
                                        return activityHour === hour.getHours();
                                      })
                                      .map((activity, actIndex) => {
                                        const activityType = activityTypes.find(t => t.value === activity.activity_type) || {};
                                        
                                        // Calculate duration in minutes
                                        const [startHour, startMin] = activity.start_time.split(':').map(Number);
                                        const [endHour, endMin] = activity.end_time.split(':').map(Number);
                                        const startMinutes = startHour * 60 + startMin;
                                        const endMinutes = endHour * 60 + endMin;
                                        const durationMinutes = endMinutes - startMinutes;
                                        
                                        return (
                                          <div
                                            key={`act-${actIndex}`}
                                            className="text-[10px] rounded px-1 py-0.5 mt-1 cursor-pointer"
                                            style={{ 
                                              backgroundColor: activityType.color + '30',
                                              border: `1px solid ${activityType.color}`,
                                              color: activityType.color
                                            }}
                                            onClick={() => {
                                              setEditingActivity(activity);
                                              setActivityForm({
                                                ...activity,
                                                user_ids: activity.user_ids || [],
                                                description: activity.description || '',
                                                location: activity.location || '',
                                                client_name: activity.client_name || '',
                                                all_day: activity.all_day || false,
                                                end_date: activity.end_date || '',
                                              });
                                              setShowActivityModal(true);
                                            }}
                                            title={`${activity.title}\n${activity.start_time}-${activity.end_time} (${durationMinutes} min)\n${activity.client_name || ''}`}
                                          >
                                            <div className="font-semibold truncate flex items-center space-x-0.5">
                                              <span>{activityType.icon || '📅'}</span>
                                              <span className="truncate">{activity.title}</span>
                                            </div>
                                            <div className="text-[9px] opacity-80">
                                              {activity.start_time}-{activity.end_time} ({durationMinutes}m)
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  
                  {/* Legend */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Legend</h3>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-xs text-gray-700">Regular Shift</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-orange-500 rounded"></div>
                        <span className="text-xs text-gray-700">On-Call</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                        <span className="text-xs text-gray-700">PTO</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-red-500 rounded"></div>
                        <span className="text-xs text-gray-700">Sick Leave</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-purple-500 rounded"></div>
                        <span className="text-xs text-gray-700">Vacation</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-green-500 rounded"></div>
                        <span className="text-xs text-gray-700">WFH</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-blue-600 rounded flex items-center justify-center">
                          <Home className="h-2.5 w-2.5 text-white" />
                        </div>
                        <span className="text-xs text-gray-700">🏠 WFH Override (Office → Remote)</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ArrowLeftRight className="h-4 w-4 text-gray-600" />
                        <span className="text-xs text-gray-700">Swapped Shift</span>
                      </div>
                    </div>
                  </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Monthly Calendar View */
        <MonthlyCalendar
          isManagerOrAdmin={isManagerOrAdmin}
          onNavigateToWeek={(dateStr) => {
            const d = new Date(dateStr + 'T12:00:00');
            setCurrentDate(d);
            setViewMode('weekly');
          }}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Schedules</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="total-schedules-count">
                  {schedules.length}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Regular Shifts</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="regular-shifts-count">
                  {schedules.filter(s => s.shift_type === 'regular').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">On-Call Shifts</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="oncall-shifts-count">
                  {schedules.filter(s => s.shift_type === 'on_call').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deletion functionality moved to Admin section */}
      
      {/* Drag-and-Drop Visual Feedback */}
      {isDragging && draggedShift && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">
                Moving {draggedShift.employee?.name}'s shift ({formatTime(draggedShift.start_time)}-{formatTime(draggedShift.end_time)})
              </span>
            </div>
            <p className="text-xs text-blue-200 mt-1">Drop on any cell to create a new shift</p>
          </div>
        </div>
      )}

      {/* Drop resolution prompt — appears when a shift is dropped onto an occupied cell */}
      {dropDecision && (
        <Dialog open={!!dropDecision} onOpenChange={(o) => { if (!o) setDropDecision(null); }}>
          <DialogContent className="max-w-md" data-testid="drop-decision-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                {dropDecision.targetEmployee.name} already has a shift here
              </DialogTitle>
              <DialogDescription className="text-sm">
                You're moving <span className="font-semibold">{dropDecision.draggedShift.employee?.name}</span>'s{' '}
                {formatTime(dropDecision.draggedShift.start_time)}–{formatTime(dropDecision.draggedShift.end_time)}{' '}
                {dropDecision.draggedShift.shift_type === 'on_call' ? 'on-call' : 'regular'} shift onto{' '}
                <span className="font-semibold">{dropDecision.targetEmployee.name}</span> on{' '}
                {dropDecision.targetDay.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}.
                How should we handle the {dropDecision.existingShifts.length} shift{dropDecision.existingShifts.length > 1 ? 's' : ''} already there?
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
              <p className="text-[11px] font-bold uppercase text-gray-400">Currently in that cell</p>
              {dropDecision.existingShifts.map((ex) => (
                <div key={ex.id} className="text-sm font-medium text-gray-700" data-testid={`drop-existing-${ex.id}`}>
                  {formatTime(ex.start_time)}–{formatTime(ex.end_time)}
                  <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${ex.shift_type === 'on_call' ? 'bg-orange-500 text-white' : 'bg-blue-600 text-white'}`}>
                    {ex.shift_type === 'on_call' ? 'On-Call' : 'Regular'}
                  </span>
                </div>
              ))}
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => applyDrop(dropDecision, 'swap')}
                data-testid="drop-swap-btn"
              >
                Swap shifts <span className="ml-1 text-purple-200 text-xs font-normal">— trade places, nothing lost</span>
              </Button>
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => applyDrop(dropDecision, 'replace')}
                data-testid="drop-replace-btn"
              >
                Replace existing <span className="ml-1 text-red-400 text-xs font-normal">— deletes the current shift</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-600 hover:bg-gray-100"
                onClick={() => applyDrop(dropDecision, 'add')}
                data-testid="drop-add-btn"
              >
                Keep both <span className="ml-1 text-gray-400 text-xs font-normal">— stack them in the cell</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-400 hover:bg-gray-100"
                onClick={() => setDropDecision(null)}
                data-testid="drop-cancel-btn"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Copy Week overlap resolution modal */}
      {copyOverlap.open && (
        <Dialog open={copyOverlap.open} onOpenChange={(o) => { if (!o) setCopyOverlap({ ...copyOverlap, open: false }); }}>
          <DialogContent className="max-w-md" data-testid="copy-overlap-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                {copyOverlap.conflicts.length} shift{copyOverlap.conflicts.length > 1 ? 's' : ''} would overlap existing shifts
              </DialogTitle>
              <DialogDescription className="text-sm">
                Some employees already have shifts on the target dates. Choose how to handle the overlaps.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <button
                type="button"
                className="text-xs font-semibold text-blue-600 hover:underline"
                onClick={() => setCopyOverlap({ ...copyOverlap, expanded: !copyOverlap.expanded })}
                data-testid="copy-overlap-toggle-list"
              >
                {copyOverlap.expanded ? 'Hide' : 'Show'} conflicting shifts ({copyOverlap.conflicts.length})
              </button>
              {copyOverlap.expanded && (
                <div className="mt-2 max-h-48 overflow-y-auto space-y-2" data-testid="copy-overlap-list">
                  {copyOverlap.conflicts.map((c, i) => {
                    const emp = employees.find(e => e.id === c.newSchedule.user_id);
                    return (
                      <div key={i} className="text-xs border-b border-gray-100 pb-1.5" data-testid={`copy-overlap-item-${i}`}>
                        <div className="font-medium text-gray-800">{emp?.name || 'Unknown'} — {c.newSchedule.date}</div>
                        <div className="text-gray-500">
                          New: {formatTime(c.newSchedule.start_time)}–{formatTime(c.newSchedule.end_time)}
                          <span className="mx-1">vs</span>
                          existing: {c.overlaps.map(ex => `${formatTime(ex.start_time)}–${formatTime(ex.end_time)}`).join(', ')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => resolveCopyOverlap('replace')}
                data-testid="copy-overlap-replace-btn"
              >
                Replace all <span className="ml-1 text-red-400 text-xs font-normal">— delete existing, use copied shift</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-600 hover:bg-gray-100"
                onClick={() => resolveCopyOverlap('keep_both')}
                data-testid="copy-overlap-keepboth-btn"
              >
                Keep both <span className="ml-1 text-gray-400 text-xs font-normal">— stack copied shifts alongside</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-600 hover:bg-gray-100"
                onClick={() => resolveCopyOverlap('skip')}
                data-testid="copy-overlap-skip-btn"
              >
                Skip overlaps <span className="ml-1 text-gray-400 text-xs font-normal">— copy only non-conflicting shifts</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-400 hover:bg-gray-100"
                onClick={() => setCopyOverlap({ open: false, plan: [], conflicts: [], headers: null, modeText: '', expanded: false })}
                data-testid="copy-overlap-cancel-btn"
              >
                Cancel copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}



      {/* Clear Employee Schedule Modal */}
      <Dialog open={showClearScheduleModal} onOpenChange={setShowClearScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clear Schedule for {employeeToClear?.name}</DialogTitle>
            <DialogDescription>
              Choose which schedules to remove for this employee. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Range Options */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="week"
                  checked={clearScheduleRange === 'week'}
                  onChange={(e) => setClearScheduleRange(e.target.value)}
                  className="rounded-full"
                />
                <span className="text-sm">
                  This week only ({schedules.filter(s => s.user_id === employeeToClear?.id && weekDays.map(d => d.toISOString().split('T')[0]).includes(s.date)).length} shifts)
                </span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="all"
                  checked={clearScheduleRange === 'all'}
                  onChange={(e) => setClearScheduleRange(e.target.value)}
                  className="rounded-full"
                />
                <span className="text-sm">
                  All schedules ({schedules.filter(s => s.user_id === employeeToClear?.id).length} shifts)
                </span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="custom"
                  checked={clearScheduleRange === 'custom'}
                  onChange={(e) => setClearScheduleRange(e.target.value)}
                  className="rounded-full"
                />
                <span className="text-sm">Custom date range</span>
              </label>
            </div>

            {/* Custom Date Range */}
            {clearScheduleRange === 'custom' && (
              <div className="space-y-2 pl-6">
                <div>
                  <Label htmlFor="clear-start-date" className="text-xs">Start Date</Label>
                  <Input
                    id="clear-start-date"
                    type="date"
                    value={clearScheduleDateRange.start}
                    onChange={(e) => setClearScheduleDateRange({...clearScheduleDateRange, start: e.target.value})}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="clear-end-date" className="text-xs">End Date</Label>
                  <Input
                    id="clear-end-date"
                    type="date"
                    value={clearScheduleDateRange.end}
                    onChange={(e) => setClearScheduleDateRange({...clearScheduleDateRange, end: e.target.value})}
                    className="text-sm"
                  />
                </div>
                {clearScheduleDateRange.start && clearScheduleDateRange.end && (
                  <p className="text-xs text-gray-600">
                    {schedules.filter(s => 
                      s.user_id === employeeToClear?.id && 
                      s.date >= clearScheduleDateRange.start && 
                      s.date <= clearScheduleDateRange.end
                    ).length} shifts in this range
                  </p>
                )}
              </div>
            )}

            {/* Shift Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Shift Type</Label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="all"
                    checked={clearScheduleShiftType === 'all'}
                    onChange={(e) => setClearScheduleShiftType(e.target.value)}
                    className="rounded-full"
                  />
                  <span className="text-sm">All shifts (regular + on-call)</span>
                </label>
                
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="regular"
                    checked={clearScheduleShiftType === 'regular'}
                    onChange={(e) => setClearScheduleShiftType(e.target.value)}
                    className="rounded-full"
                  />
                  <span className="text-sm">Regular shifts only</span>
                </label>
                
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="on_call"
                    checked={clearScheduleShiftType === 'on_call'}
                    onChange={(e) => setClearScheduleShiftType(e.target.value)}
                    className="rounded-full"
                  />
                  <span className="text-sm">On-call shifts only</span>
                </label>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will permanently delete the selected schedules. This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowClearScheduleModal(false);
                setEmployeeToClear(null);
                setClearScheduleRange('week');
                setClearScheduleDateRange({ start: '', end: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearEmployeeSchedule}
              disabled={clearScheduleRange === 'custom' && (!clearScheduleDateRange.start || !clearScheduleDateRange.end)}
            >
              Clear Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rotation Management Modal */}
      <Dialog open={showRotationsModal} onOpenChange={setShowRotationsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <RotationManagement 
            isManagerOrAdmin={isManagerOrAdmin}
            currentUser={user}
          />
        </DialogContent>
      </Dialog>

      {/* Schedule Conflict Modal */}
      <ScheduleConflictModal
        open={showConflictModal}
        onClose={() => {
          setShowConflictModal(false);
          setPendingScheduleData(null);
          setDetectedConflicts(null);
          resetForm(); // Reset form when modal is cancelled
        }}
        conflicts={detectedConflicts}
        onOverwrite={handleConflictOverwrite}
        onAddAnyway={handleConflictAddAnyway}
        onSkipConflicts={handleConflictSkip}
      />

      {/* Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingActivity ? 'Edit Activity' : 'Add Activity'}
            </DialogTitle>
            <DialogDescription>
              Schedule a client visit, conference call, training, or other activity
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleActivitySubmit}>
            <div className="space-y-4">
              {/* Activity Type */}
              <div>
                <Label htmlFor="activity-type">Activity Type *</Label>
                <Select
                  value={activityForm.activity_type || undefined}
                  onValueChange={(value) => setActivityForm({...activityForm, activity_type: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select activity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.filter(type => type.value).map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center space-x-2">
                          <span>{type.icon}</span>
                          <span>{type.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Participants - Only show when creating new (not editing) and user is manager/admin */}
              {!editingActivity && isManagerOrAdmin && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="activity-participants">Participants (Optional - for team events)</Label>
                      <p className="text-xs text-gray-500">Select multiple people for team training, group meetings, etc.</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const activeEmployeeIds = employees
                            .filter(emp => emp.is_active)
                            .map(emp => emp.id);
                          setActivityForm({
                            ...activityForm,
                            user_ids: activeEmployeeIds,
                            user_id: '' // Clear single user if multi-select used
                          });
                        }}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActivityForm({
                            ...activityForm,
                            user_ids: []
                          });
                        }}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
                    {employees
                      .filter(emp => emp.is_active)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(emp => (
                        <label key={emp.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activityForm.user_ids.includes(emp.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setActivityForm({
                                  ...activityForm,
                                  user_ids: [...activityForm.user_ids, emp.id],
                                  user_id: '' // Clear single user if multi-select used
                                });
                              } else {
                                setActivityForm({
                                  ...activityForm,
                                  user_ids: activityForm.user_ids.filter(id => id !== emp.id)
                                });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{emp.name} ({emp.role?.toUpperCase()})</span>
                        </label>
                      ))}
                  </div>
                  {activityForm.user_ids.length > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      Selected: {activityForm.user_ids.length} participant{activityForm.user_ids.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Title */}
              <div>
                <Label htmlFor="activity-title">Title *</Label>
                <Input
                  id="activity-title"
                  value={activityForm.title}
                  onChange={(e) => setActivityForm({...activityForm, title: e.target.value})}
                  placeholder="e.g., Visit ABC Corp, Team Meeting"
                  required
                />
              </div>

              {/* Date, All Day, and Time */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="activity-date">{activityForm.end_date ? 'Start Date *' : 'Date *'}</Label>
                    <Input
                      id="activity-date"
                      type="date"
                      value={activityForm.date}
                      onChange={(e) => setActivityForm({...activityForm, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="activity-end-date">End Date (for multi-day)</Label>
                    <Input
                      id="activity-end-date"
                      type="date"
                      value={activityForm.end_date || ''}
                      min={activityForm.date}
                      onChange={(e) => setActivityForm({...activityForm, end_date: e.target.value})}
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activityForm.all_day || false}
                    onChange={(e) => setActivityForm({
                      ...activityForm,
                      all_day: e.target.checked,
                      start_time: e.target.checked ? '00:00' : '09:00',
                      end_time: e.target.checked ? '23:59' : '10:00',
                    })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">All day</span>
                </label>

                {!activityForm.all_day && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="activity-start-time">Start Time *</Label>
                      <Input
                        id="activity-start-time"
                        type="time"
                        value={activityForm.start_time}
                        onChange={(e) => setActivityForm({...activityForm, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="activity-end-time">End Time *</Label>
                      <Input
                        id="activity-end-time"
                        type="time"
                        value={activityForm.end_time}
                        onChange={(e) => setActivityForm({...activityForm, end_time: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Client Name (Optional) */}
              <div>
                <Label htmlFor="activity-client">Client Name (Optional)</Label>
                <Input
                  id="activity-client"
                  value={activityForm.client_name}
                  onChange={(e) => setActivityForm({...activityForm, client_name: e.target.value})}
                  placeholder="e.g., ABC Corporation"
                />
              </div>

              {/* Location (Optional) */}
              <div>
                <Label htmlFor="activity-location">Location (Optional)</Label>
                <Input
                  id="activity-location"
                  value={activityForm.location}
                  onChange={(e) => setActivityForm({...activityForm, location: e.target.value})}
                  placeholder="e.g., 123 Main St, Conference Room A"
                />
              </div>

              {/* Description (Optional) */}
              <div>
                <Label htmlFor="activity-description">Description (Optional)</Label>
                <Textarea
                  id="activity-description"
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                  placeholder="Add any additional details..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              {editingActivity && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => handleDeleteActivity(editingActivity.id)}
                >
                  Delete Activity
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setShowActivityModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingActivity ? 'Update Activity' : 'Create Activity'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};


export default ScheduleManager;